/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from "react";
import html2canvas from 'html2canvas';
import ShareCardPeriod from './ShareCardPeriod.js';
import { getMedal as getMedalByLevel } from './medalsLogic.js';
import { getLevelFromXP } from './xpSystem.js';
import { EXERCISE_DATABASE, EXERCISE_CATEGORIES, EXERCISE_GUIDE, getExCategorie, TABATA_EX_BASE } from './exercises.js';
import { LOGO_LIGHT_DATA, MASCOT_BUST_DATA, NAV_ICON_MUSCU, NAV_ICON_TABATA, NAV_ICON_STATS, NAV_ICON_PROFILE } from './assets.js';

// ─── CONSTANTES ───────────────────────────────────────────────────────────────
const MENSURATION_KEYS = [
  {k:"neck",       label:"Cou"},
  {k:"chest",      label:"Poitrine"},
  {k:"shoulder",   label:"Épaules"},
  {k:"waist",      label:"Taille"},
  {k:"hips",       label:"Hanches"},
  {k:"armLeft",    label:"Bras gauche"},
  {k:"armRight",   label:"Bras droit"},
  {k:"thighLeft",  label:"Jambe gauche"},
  {k:"thighRight", label:"Jambe droite"},
  {k:"calfLeft",   label:"Mollet gauche"},
  {k:"calfRight",  label:"Mollet droit"},
];

const showToast = (message, type = "info") => {
  const toast = document.createElement("div");
  toast.textContent = message;
  toast.style.cssText = `position:fixed;bottom:100px;left:50%;transform:translateX(-50%);background:${type==="error"?"#ef4444":"#3b82f6"};color:white;padding:10px 20px;border-radius:12px;font-weight:700;font-size:14px;z-index:9999;pointer-events:none;`;
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
};

// ─── UI ATOMS ─────────────────────────────────────────────────────────────────
const Tag = ({label,color,bg}) => (
  <span style={{background:bg,color,fontSize:11,padding:"3px 10px",borderRadius:20,fontWeight:700}}>{label}</span>
);
const MM = {
  "Pectoraux":{color:"#ef4444",bg:"rgba(239,68,68,0.12)"},
  "Dos":{color:"#8b5cf6",bg:"rgba(139,92,246,0.12)"},
  "Épaules":{color:"#eab308",bg:"rgba(234,179,8,0.12)"},
  "Biceps":             {color:"#f97316",bg:"rgba(249,115,22,0.12)"},
  "Triceps":            {color:"#06b6d4",bg:"rgba(6,182,212,0.12)"},
  "Quadriceps":         {color:"#3b82f6",bg:"rgba(59,130,246,0.12)"},
  "Ischio-jambiers":    {color:"#10b981",bg:"rgba(16,185,129,0.12)"},
  "Fessiers":           {color:"#ec4899",bg:"rgba(236,72,153,0.12)"},
  "Mollets":            {color:"#14b8a6",bg:"rgba(20,184,166,0.12)"},
  "Abdominaux":         {color:"#f59e0b",bg:"rgba(245,158,11,0.12)"},
  "Trapèzes":           {color:"#a78bfa",bg:"rgba(167,139,250,0.12)"},
  "Avant-bras":         {color:"#fb923c",bg:"rgba(251,146,60,0.12)"},
  "Cardio / Full body": {color:"#84cc16",bg:"rgba(132,204,22,0.12)"},
};
const ConfirmModal = ({message,onConfirm,onCancel,confirmLabel,cancelLabel,C}) => (
  <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.85)",backdropFilter:"blur(8px)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:400,padding:"0 24px"}}>
    <div style={{background:C.card,borderRadius:20,padding:"28px 24px",width:"100%",maxWidth:380,border:`1px solid ${C.border}`}}>
      <p style={{color:C.text,fontWeight:800,fontSize:17,margin:"0 0 8px",textAlign:"center"}}>Confirmer</p>
      <p style={{color:C.muted,fontSize:14,margin:"0 0 24px",textAlign:"center",lineHeight:1.5}}>{message}</p>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
        <button onClick={onCancel} style={{padding:"12px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.muted,fontWeight:800,cursor:"pointer"}}>{cancelLabel||"Annuler"}</button>
        <button onClick={onConfirm} style={{padding:"12px",background:confirmLabel?"rgba(59,130,246,0.12)":"rgba(239,68,68,0.12)",border:confirmLabel?"1px solid rgba(59,130,246,0.3)":"1px solid rgba(239,68,68,0.3)",borderRadius:12,color:confirmLabel?C.accent:C.red,fontWeight:800,cursor:"pointer"}}>{confirmLabel||"Supprimer"}</button>
      </div>
    </div>
  </div>
);

function LineChart({data, valueKey="val", labelKey="date", height=88, accentColor, mutedColor, cardBg, borderColor, compact=false}) {
  if(!data||data.length<2) return null;
  const vals = data.map(d=>d[valueKey]);
  const minV = Math.min(...vals);
  const maxV = Math.max(...vals);
  const range = maxV-minV || 1;
  const W = 300, H = compact ? 38 : height-24;
  const pad = compact ? 6 : 10;
  const n = data.length;
  const xs = data.map((_,i)=> pad + (i/(n-1))*(W-pad*2));
  const ys = vals.map(v=> H - pad - ((v-minV)/range)*(H-pad*2));
  // Polyline path
  const pts = xs.map((x,i)=>`${x},${ys[i]}`).join(" ");
  // Area fill path
  const areaPath = `M ${xs[0]},${ys[0]} ` + xs.slice(1).map((x,i)=>`L ${x},${ys[i+1]}`).join(" ") + ` L ${xs[n-1]},${H-pad} L ${xs[0]},${H-pad} Z`;

  if(compact) {
    return (
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} style={{display:"block",overflow:"visible"}}>
        <defs>
          <linearGradient id="cgA" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={accentColor} stopOpacity="0.25"/>
            <stop offset="100%" stopColor={accentColor} stopOpacity="0"/>
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#cgA)"/>
        <polyline points={pts} fill="none" stroke={accentColor} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round"/>
        {xs.map((x,i)=>(
          <circle key={i} cx={x} cy={ys[i]} r={i===n-1?4:2.5}
            fill={i===n-1?accentColor:cardBg||"#1a1a1a"} stroke={i===n-1?accentColor:accentColor+"88"} strokeWidth="1.5"/>
        ))}
      </svg>
    );
  }

  return (
    <svg viewBox={`0 0 ${W} ${H+24}`} width="100%" height={H+24} style={{display:"block",overflow:"visible"}}>
      <defs>
        <linearGradient id="lgA" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accentColor} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={accentColor} stopOpacity="0"/>
        </linearGradient>
      </defs>
      {/* Grille horizontale légère */}
      {[0.25,0.5,0.75].map((f,i)=>(
        <line key={i} x1={pad} y1={pad+(1-f)*(H-pad*2)} x2={W-pad} y2={pad+(1-f)*(H-pad*2)}
          stroke={borderColor||"#333"} strokeWidth="0.5" strokeDasharray="3,4" opacity="0.5"/>
      ))}
      {/* Zone remplie */}
      <path d={areaPath} fill="url(#lgA)"/>
      {/* Ligne courbe */}
      <polyline points={pts} fill="none" stroke={accentColor} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round"/>
      {/* Points + valeurs */}
      {xs.map((x,i)=>{
        const isLast = i===n-1;
        return (
          <g key={i}>
            <circle cx={x} cy={ys[i]} r={isLast?5:3.5}
              fill={isLast?accentColor:cardBg||"#1a1a1a"} stroke={isLast?accentColor:accentColor+"99"} strokeWidth="2"/>
            {isLast&&<circle cx={x} cy={ys[i]} r={8} fill={accentColor} opacity="0.15"/>}
            <text x={x} y={ys[i]-9} textAnchor="middle" fontSize="9" fill={isLast?accentColor:mutedColor||"#666"} fontWeight={isLast?"700":"400"}>
              {vals[i]}
            </text>
            <text x={x} y={H+18} textAnchor="middle" fontSize="9" fill={mutedColor||"#666"}>
              {data[i][labelKey]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function MovementGuide({exercise, onClose, C, inlineMode=false}) {
  // Support both {name, detail} objects and plain string (from inlineMode usage)
  const exName = typeof exercise === "string" ? exercise : exercise?.name;
  const exDetail = typeof exercise === "string" ? null : exercise?.detail;
  const guide = EXERCISE_GUIDE[exName];
  
  // Exercices sensibles nécessitant un avertissement de sécurité
  const riskyExercises = [
    "Squat barre",
    "Squat avant",
    "Développé couché barre",
    "Développé couché haltères",
    "Développé incliné barre",
    "Soulevé de terre",
    "Soulevé de terre roumain",
    "Développé militaire barre",
    "Back squat",
    "Front squat",
    "Nordic curl",
    "Toes to bar",
    "Pompes claquées",
    "Fentes bulgares",
    "Snatch kettlebell",
  ];
  
  const isRisky = riskyExercises.some(r => exName?.toLowerCase().includes(r.toLowerCase()));

  const content = (
    <div style={{padding: inlineMode?"0":"0 20px 60px"}}>
      {!inlineMode&&(
        <div style={{background:C.faint,borderRadius:16,marginBottom:16,height:160,border:`1px solid ${C.border}`}}/>

      )}

      {exDetail&&(
        <div style={{background:`${C.accent}12`,borderRadius:12,padding:"10px 14px",marginBottom:16,border:`1px solid ${C.accent}30`}}>
          <p style={{color:C.accent,fontWeight:700,fontSize:11,margin:"0 0 3px",letterSpacing:1}}>MUSCLES CIBLÉS</p>
          <p style={{color:C.text,fontSize:13,margin:0,lineHeight:1.5}}>{exDetail}</p>
        </div>
      )}

      {guide ? (
        <>
          <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 12px"}}>EXÉCUTION</p>
          <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16}}>
            {guide.steps.map((step,i)=>(
              <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                <div style={{width:26,height:26,borderRadius:"50%",background:`${C.accent}20`,border:`1px solid ${C.accent}50`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
                  <span style={{color:C.accent,fontSize:12,fontWeight:900}}>{i+1}</span>
                </div>
                <p style={{color:C.text,fontSize:14,lineHeight:1.55,margin:0,flex:1}}>{step}</p>
              </div>
            ))}
          </div>
          <div style={{background:C.card,borderRadius:12,padding:"12px 14px",border:`1px solid ${C.border}`,display:"flex",gap:10,alignItems:"flex-start",marginBottom:isRisky?12:0}}>
            <p style={{color:C.muted,fontSize:13,margin:0,lineHeight:1.55}}>{guide.tips}</p>
          </div>
          {isRisky&&(
            <div style={{background:"#FEE",borderRadius:12,padding:"12px 14px",border:"2px solid #F44",display:"flex",gap:10,alignItems:"flex-start"}}>
              <div>
                <p style={{color:"#C00",fontWeight:800,fontSize:13,margin:"0 0 4px"}}>ATTENTION - Exercice à risque</p>
                <p style={{color:"#800",fontSize:12,margin:0,lineHeight:1.55}}>Cet exercice présente des risques accrus de blessure. Assurez-vous de maîtriser parfaitement la technique avant d'augmenter les charges. En cas de doute, demandez conseil à un coach.</p>
              </div>
            </div>
          )}
        </>
      ):(
        <div style={{background:C.card,borderRadius:12,padding:"16px",border:`1px solid ${C.border}`}}>
          <p style={{color:C.muted,fontSize:13,margin:0,lineHeight:1.6}}>Description bientôt disponible.</p>
        </div>
      )}
    </div>
  );

  if(inlineMode){
    return(
      <div style={{background:C.card,borderRadius:18,border:`1px solid ${C.accent}30`,padding:"16px",marginBottom:16}}>
        <p style={{color:C.accent,fontSize:10,letterSpacing:3,margin:"0 0 12px",fontWeight:700}}>COMMENT RÉALISER CET EXERCICE</p>
        {content}
      </div>
    );
  }
  
  return (
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,zIndex:500,display:"flex",flexDirection:"column",overflowY:"auto"}}>
      <div style={{background:`linear-gradient(180deg,${C.accent}18,transparent)`,padding:"calc(env(safe-area-inset-top) + 20px) 20px 20px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{flex:1,minWidth:0,paddingRight:12}}>
          <p style={{color:C.accent,fontSize:10,letterSpacing:3,margin:"0 0 6px",fontWeight:700}}>COMMENT RÉALISER</p>
          <h2 style={{color:C.text,fontSize:20,fontWeight:900,margin:0,lineHeight:1.3}}>{exName}</h2>
        </div>
        <button onClick={onClose} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:"50%",width:40,height:40,color:C.text,fontSize:22,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>×</button>
      </div>
      {content}
    </div>
  );
}

function MeasModal({onAddMeasurement,onClose,C}) {
  const [vals,setVals]=useState({});const [weight,setWeight]=useState("");
  const set=(k,v)=>setVals(p=>({...p,[k]:v}));
  const save=()=>{onAddMeasurement({date:new Date().toISOString(),weight:weight?parseFloat(weight):null,...vals});onClose();};
  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.isDark?"rgba(0,0,0,0.9)":C.bg,backdropFilter:"blur(10px)",zIndex:300,display:"flex",flexDirection:"column"}}>
      <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 12px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onClose} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
        <h2 style={{color:C.text,fontSize:18,fontWeight:900,margin:0}}>AJOUTER UNE MESURE</h2>
      </div>
      <div style={{flex:1,overflowY:"auto",minHeight:0,WebkitOverflowScrolling:"touch",padding:"0 20px calc(env(safe-area-inset-bottom) + 100px)"}}>
        <p style={{color:C.muted,fontSize:12,margin:"0 0 16px"}}>Laissez vide les champs non mesurés.</p>
        <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:7}}>POIDS CORPOREL (kg)</label>
        <input type="number" value={weight} onChange={e=>setWeight(e.target.value)} placeholder="ex: 82.5"
          style={{width:"100%",background:C.surface,border:`1px solid ${C.accent}`,borderRadius:12,color:C.text,fontSize:16,padding:"11px 13px",outline:"none",boxSizing:"border-box",marginBottom:18}}/>
        <p style={{color:C.muted,fontSize:11,letterSpacing:2,margin:"0 0 12px"}}>MENSURATIONS (cm)</p>
        {MENSURATION_KEYS.map(({k,label})=>(
          <div key={k} style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
            <span style={{color:C.text,fontSize:13,fontWeight:600,flex:1,minWidth:120}}>{label}</span>
            <input type="number" value={vals[k]||""} onChange={e=>set(k,e.target.value)} placeholder="—"
              style={{width:90,background:C.surface,border:`1px solid ${C.border}`,borderRadius:10,color:C.accent,fontSize:16,fontWeight:700,padding:"9px 10px",outline:"none",textAlign:"center"}}/>
          </div>
        ))}
        <button onClick={save} style={{width:"100%",marginTop:20,padding:"14px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,color:"white",fontWeight:800,fontSize:15,cursor:"pointer"}}>ENREGISTRER</button>
      </div>
    </div>
  );
}
function StatsPage({sessions,measurements,onAddMeasurement,onDeleteMeasurement,prs,onSavePR,onDeletePR,initialTab,onBack,C}) {
  const [tab,setTab]=useState(initialTab||"poids");
  const [selEx,setSelEx]=useState(null);
  const [showMeasModal,setShowMeasModal]=useState(false);
  const [showPRModal,setShowPRModal]=useState(false);
  const [selPR,setSelPR]=useState(null);
  const [selMens,setSelMens]=useState(null);
  const [mensToDelete,setMensToDelete]=useState(null);
  const [confirmDel,setConfirmDel]=useState(null); // {type:"pr"|"mens"|"prEntry"|"mensEntry", payload}
  const [prEx,setPREx]=useState("");
  const [prWeight,setPRWeight]=useState(0);
  const [prRaw,setPRRaw]=useState("0");
  // Suppression d'une mesure individuelle
  useEffect(()=>{
    if(!mensToDelete)return;
    const{key,date}=mensToDelete;
    onDeleteMeasurement(key,date);
    // Rafraîchir selMens après suppression
    setSelMens(prev=>{
      if(!prev||prev.key!==key)return prev;
      // Vérifier s'il reste des données pour cette clé
      return {...prev}; // force re-render — les pts sont recalculés depuis measurements
    });
    setMensToDelete(null);
  },[mensToDelete]);

  // Exécution de la suppression confirmée
  const execConfirm=()=>{
    if(!confirmDel)return;
    const{type,payload}=confirmDel;
    if(type==="mensEntry"){
      setMensToDelete({key:payload.key,date:payload.date});
      // Si c'est la dernière mesure de cette clé, fermer la vue détail
      const remaining=measurements.filter(m=>m.date!==payload.date&&m[payload.key]);
      if(remaining.length===0)setSelMens(null);
    } else if(type==="prEntry"){
      const{name,realIdx,history}=payload;
      const newHistory=history.filter((_,j)=>j!==realIdx);
      if(newHistory.length===0){onDeletePR(name);setSelPR(null);}
      else{
        const newCurrent=newHistory.at(-1);
        const newPast=newHistory.slice(0,-1);
        onSavePR(name,{weight:newCurrent.weight,date:newCurrent.date,history:newPast});
        setSelPR({name,pr:{weight:newCurrent.weight,date:newCurrent.date,history:newPast}});
      }
    }
    setConfirmDel(null);
  };

  const PR_EXERCISES=[
    "Développé couché à la barre",
    "Squat",
    "Soulevé de terre",
    "Développé militaire",
    "Tractions lestées",
    "Rowing barre",
    "Hip thrust",
    "Dips lestés",
  ];
  const muscuSessions=sessions.filter(s=>s.type==="muscu"||!s.type);
  const hist={};
  muscuSessions.forEach(s=>{
    const dl=new Date(s.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"});
    s.exercises.forEach(ex=>{
      if(!ex.sets?.length)return;
      const wt=ex.sets.filter(st=>st.weight>0);if(!wt.length)return;
      const mw=Math.max(...wt.map(st=>st.weight));
      if(!hist[ex.name])hist[ex.name]=[];
      hist[ex.name].push({date:dl,fullDate:s.date,maxWeight:mw,sets:ex.sets});
    });
  });
  const exList=Object.entries(hist).map(([name,data])=>({name,data:data.sort((a,b)=>new Date(a.fullDate)-new Date(b.fullDate))})).sort((a,b)=>b.data.length-a.data.length);


  // ── Vue détail Mensuration ────────────────────────────────────────────────
  if(selMens){
    const{key,label}=selMens;
    // key==="weight" → poids corporel, sinon mensuration
    const pts=(key==="weight"
      ?measurements.filter(m=>m.weight).map(m=>({val:m.weight,date:m.date}))
      :measurements.filter(m=>m[key]).map(m=>({val:parseFloat(m[key]),date:m.date}))
    ).sort((a,b)=>new Date(a.date)-new Date(b.date));
    const unit=key==="weight"?"kg":"cm";
    const maxV=Math.max(...pts.map(p=>p.val),1);
    const prog=pts.length>=2?+(pts.at(-1).val-pts[0].val).toFixed(1):null;
    return(
      <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
        <div style={{padding:"0 0 16px",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setSelMens(null)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
          <div>
            <h2 style={{color:C.text,fontSize:17,fontWeight:900,margin:0}}>{label}</h2>
            <p style={{color:C.muted,fontSize:12,margin:0}}>{pts.length} mesure{pts.length>1?"s":""}</p>
          </div>
        </div>
        {/* Stats résumé */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"ACTUEL",      v:`${pts.at(-1).val} ${unit}`, c:C.accent},
            {l:"ÉVOLUTION",   v:prog!==null?`${prog>0?"+":""}${prog} ${unit}`:"—", c:prog<0?C.green:prog>0?C.red:C.muted},
            {l:"MESURES",     v:pts.length},
          ].map(s=>(
            <div key={s.l} style={{background:C.card,borderRadius:13,padding:"12px 10px",textAlign:"center",border:`1px solid ${C.border}`}}>
              <p style={{color:s.c||C.accent,fontSize:16,fontWeight:900,margin:"0 0 2px"}}>{s.v}</p>
              <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>{s.l}</p>
            </div>
          ))}
        </div>
        {/* Courbe */}
        {pts.length>1&&(
          <div style={{background:C.card,borderRadius:16,padding:"14px 14px 8px",border:`1px solid ${C.border}`,marginBottom:16}}>
            <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>ÉVOLUTION ({unit})</p>
            <LineChart
              data={pts.slice(-10).map(p=>({val:p.val,date:new Date(p.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}))}
              height={96} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}
            />
          </div>
        )}
        {/* Liste chronologique inversée */}
        <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>HISTORIQUE</p>
        {[...pts].reverse().map((p,i)=>{
          const isLatest=i===0;
          const handleDeleteEntry=()=>{
            setConfirmDel({type:"mensEntry",payload:{key,date:p.date,val:p.val,unit}});
          };
          return(
          <div key={i} style={{background:isLatest?`${C.accent}10`:C.card,borderRadius:13,padding:"12px 16px",border:`1px solid ${isLatest?C.accent+"40":C.border}`,marginBottom:7,display:"flex",alignItems:"center",gap:10}}>
            <p style={{color:C.text,fontWeight:700,fontSize:14,margin:0,flex:1}}>{new Date(p.date).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</p>
            <span style={{color:isLatest?C.accent:C.text,fontWeight:900,fontSize:20,flexShrink:0}}>{p.val} {unit}</span>
            <button onClick={handleDeleteEntry}
              style={{background:"none",border:"none",color:C.red,fontSize:18,cursor:"pointer",padding:"4px 6px",lineHeight:1,flexShrink:0}}>×</button>
          </div>
          );
        })}
      {confirmDel&&confirmDel.type==="mensEntry"&&<ConfirmModal
        message={`Supprimer la mesure du ${new Date(confirmDel.payload.date).toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"})} (${confirmDel.payload.val} ${confirmDel.payload.unit}) ?`}
        onConfirm={execConfirm}
        onCancel={()=>setConfirmDel(null)}
        C={C}/>}
      </div>
    );
  }

  // ── Vue détail PR ─────────────────────────────────────────────────────────
  if(selPR){
    const{name,pr}=selPR;
    const history=[...(pr.history||[]),{weight:pr.weight,date:pr.date}]
      .sort((a,b)=>new Date(a.date)-new Date(b.date));
    const maxW=Math.max(...history.map(h=>h.weight),1);
    const prog=history.length>=2
      ?((history.at(-1).weight-history[0].weight)/Math.max(history[0].weight,1)*100).toFixed(1)
      :null;
    return(
      <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
        <div style={{padding:"0 0 16px",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setSelPR(null)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
          <div>
            <h2 style={{color:C.text,fontSize:17,fontWeight:900,margin:0}}>{name}</h2>
            <p style={{color:C.muted,fontSize:12,margin:0}}>Record Personnel · 1 rep max</p>
          </div>
        </div>
        {/* Stats résumé */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
          {[
            {l:"PR ACTUEL",  v:`${pr.weight} kg`, c:C.accent},
            {l:"PROGRESSION",v:prog?`${prog>0?"+":""}${prog}%`:"—", c:prog>0?C.green:prog<0?C.red:C.muted},
            {l:"TENTATIVES", v:history.length},
          ].map(s=>(
            <div key={s.l} style={{background:C.card,borderRadius:13,padding:"12px 10px",textAlign:"center",border:`1px solid ${C.border}`}}>
              <p style={{color:s.c||C.accent,fontSize:16,fontWeight:900,margin:"0 0 2px"}}>{s.v}</p>
              <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>{s.l}</p>
            </div>
          ))}
        </div>
        {/* Courbe PR */}
        {history.length>1&&(
          <div style={{background:C.card,borderRadius:16,padding:"14px 14px 8px",border:`1px solid ${C.border}`,marginBottom:16}}>
            <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>ÉVOLUTION (kg)</p>
            <LineChart
              data={history.map(h=>({val:h.weight,date:new Date(h.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}))}
              height={96} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}
            />
          </div>
        )}
        {/* Liste chronologique inversée */}
        <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>HISTORIQUE</p>
        {[...history].reverse().map((h,i)=>{
          // Supprimer une entrée PR : reconstruire history sans cet index
          const realIdx=history.length-1-i;
          const isCurrentPR=realIdx===history.length-1;
          const handleDeleteEntry=()=>{
            setConfirmDel({type:"prEntry",payload:{name,realIdx,history}});
          };
          return(
          <div key={i} style={{background:isCurrentPR?`${C.accent}10`:C.card,borderRadius:13,padding:"12px 16px",border:`1px solid ${isCurrentPR?C.accent+"40":C.border}`,marginBottom:7,display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
            <div style={{flex:1,minWidth:0}}>
              <p style={{color:C.text,fontWeight:700,fontSize:14,margin:"0 0 2px"}}>{new Date(h.date).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</p>
              {isCurrentPR&&<p style={{color:C.accent,fontSize:11,fontWeight:700,margin:0}}>PR actuel</p>}
            </div>
            <span style={{color:isCurrentPR?C.accent:C.text,fontWeight:900,fontSize:20,flexShrink:0}}>{h.weight} kg</span>
            <button onClick={handleDeleteEntry}
              style={{background:"none",border:"none",color:C.red,fontSize:18,cursor:"pointer",padding:"4px 6px",lineHeight:1,flexShrink:0}}>×</button>
          </div>
          );
        })}
      {confirmDel&&confirmDel.type==="prEntry"&&<ConfirmModal
        message={`Supprimer cette entrée PR (${confirmDel.payload?.history?.[confirmDel.payload?.realIdx]?.weight} kg) ?`}
        onConfirm={execConfirm}
        onCancel={()=>setConfirmDel(null)}
        C={C}/>}
      </div>
    );
  }

  if(selEx){
    const{name,data}=selEx;
    const mw=Math.max(...data.map(d=>d.maxWeight),1);
    const prog=data.length>=2?((data.at(-1).maxWeight-data[0].maxWeight)/Math.max(data[0].maxWeight,1)*100).toFixed(1):null;
    const disp=data.slice(-10);
    const guide=EXERCISE_GUIDE[name]||null;
    return(
      <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
        <div style={{padding:"0 0 16px",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setSelEx(null)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
          <div style={{flex:1,minWidth:0}}><h2 style={{color:C.text,fontSize:17,fontWeight:900,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</h2><p style={{color:C.muted,fontSize:12,margin:0}}>{data.length} séance{data.length>1?"s":""}</p></div>
        </div>
        {/* Stats cards + 1RM */}
        {(()=>{
          // Formule d'Epley : 1RM = w × (1 + reps/30)
          const best1RM = data.flatMap(d=>d.sets||[]).filter(s=>s.weight>0&&s.reps>0).reduce((best,s)=>{
            const orm = Math.round(s.weight*(1+s.reps/30)*10)/10;
            return orm>best?orm:best;
          },0);
          return(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              {[
                {l:"MAX SÉANCE",v:`${data.at(-1).maxWeight} kg`},
                {l:"PROGRESSION",v:prog?`${prog>0?"+":""}${prog}%`:"—",c:prog>0?C.green:prog<0?C.red:C.muted},
                {l:"1RM ESTIMÉ",v:best1RM>0?`${best1RM} kg`:"—",tip:true},
                {l:"SÉANCES",v:data.length}
              ].map(s=>(
                <div key={s.l} style={{background:C.card,borderRadius:13,padding:"12px 10px",textAlign:"center",border:`1px solid ${C.border}`}}>
                  <p style={{color:s.c||C.accent,fontSize:16,fontWeight:900,margin:"0 0 2px"}}>{s.v}</p>
                  <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>{s.l}{s.tip&&<span title="Estimé via formule d'Epley"> ⓘ</span>}</p>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{background:C.card,borderRadius:16,padding:"14px 14px 8px",border:`1px solid ${C.border}`,marginBottom:16}}>
          <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>POIDS MAX (kg)</p>
          <LineChart
            data={disp.map(d=>({val:d.maxWeight,date:d.date}))}
            height={96} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}
          />
        </div>
        {/* ── Fiche exercice ── */}
        {guide&&<MovementGuide exercise={name} onClose={()=>{}} C={C} inlineMode={true}/>}
        {[...data].reverse().map((d,i)=>(
          <div key={i} style={{background:C.card,borderRadius:13,padding:"11px 13px",border:`1px solid ${C.border}`,marginBottom:7}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
              <span style={{color:C.text,fontWeight:700,fontSize:13}}>{d.date}</span>
              <span style={{color:C.accent,fontWeight:800}}>{d.maxWeight} kg</span>
            </div>
            {d.sets.map((s,j)=>(
              <div key={j} style={{display:"flex",gap:7,alignItems:"center",marginBottom:3}}>
                <span style={{background:C.faint,color:C.muted,width:18,height:18,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700}}>{j+1}</span>
                <span style={{color:C.muted,fontSize:12}}>{s.reps} reps ×</span>
                <span style={{color:s.weight>0?C.accent:C.muted,fontWeight:700,fontSize:12}}>{s.weight>0?`${s.weight} kg`:"—"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
      {showMeasModal&&<MeasModal onAddMeasurement={onAddMeasurement} onClose={()=>setShowMeasModal(false)} C={C}/>}

      <div style={{padding:"0 0 18px",display:"flex",alignItems:"center",gap:12}}>
        {onBack&&<button onClick={()=>onBack()} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",flexShrink:0}}>←</button>}
        <div>
          <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 4px"}}>SUIVI</p>
          <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>
            {tab==="poids"?"Exercices":tab==="pr"?"Records (PR)":"Mensurations"}
          </h1>
        </div>
      </div>
      {/* Onglets visibles seulement en accès direct (sans onBack) */}
      {!onBack&&<div style={{display:"flex",gap:8,marginBottom:20}}>
        {[{id:"poids",label:"Séries"},{id:"pr",label:"PR"},{id:"mens",label:"Mensurations"}].map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"10px",borderRadius:12,border:`1px solid ${tab===t.id?C.accent:C.border}`,background:tab===t.id?`${C.accent}18`:C.surface,color:tab===t.id?C.accent:C.muted,fontWeight:700,fontSize:13,cursor:"pointer"}}>{t.label}</button>
        ))}
      </div>}
      {onBack&&<div style={{marginBottom:16}}/>}
      {tab==="poids"&&(
        exList.length===0?(
          <div style={{background:C.card,borderRadius:18,padding:"44px 24px",textAlign:"center",border:`1px dashed ${C.border}`}}>
            
            <p style={{color:C.muted,fontSize:14,lineHeight:1.6}}>Vos courbes apparaîtront ici dès qu'un poids aura été enregistré.</p>
          </div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:9}}>
            {exList.map(({name,data})=>{
              const lat=data.at(-1),prev=data.length>=2?data.at(-2):null,delta=prev?lat.maxWeight-prev.maxWeight:null;
              const spark=data.slice(-6),sMax=Math.max(...spark.map(d=>d.maxWeight),1);
              return(
                <div key={name} onClick={()=>setSelEx({name,data})} style={{background:C.card,borderRadius:15,border:`1px solid ${C.border}`,padding:"12px 13px",cursor:"pointer"}}>
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:9}}>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{color:C.text,fontWeight:700,margin:"0 0 4px",fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</p>
                      <div style={{display:"flex",alignItems:"center",gap:7,marginTop:4}}>
                        <span style={{color:C.text,fontWeight:900,fontSize:17}}>{lat.maxWeight} kg</span>
                        {delta!==null&&<span style={{color:delta>0?C.green:delta<0?C.red:C.muted,fontWeight:700,fontSize:12}}>{delta>0?`+${delta}`:delta} kg</span>}
                        <span style={{color:C.muted,fontSize:11,marginLeft:"auto"}}>{data.length} séance{data.length>1?"s":""}</span>
                      </div>
                    </div>
                    <div style={{width:70,height:38,flexShrink:0}}>
                      <LineChart data={spark.map(d=>({val:d.maxWeight,date:""}))} compact={true} height={38} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}/>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
      {tab==="pr"&&(
        <div>
          {/* Bouton ajouter PR */}
          <button onClick={()=>{setPREx(PR_EXERCISES[0]);setPRWeight(0);setPRRaw("0");setShowPRModal(true);}}
            style={{width:"100%",padding:"12px",background:`${C.accent}15`,border:`1px dashed ${C.accent}55`,borderRadius:14,color:C.accent,fontWeight:800,fontSize:14,cursor:"pointer",marginBottom:16}}>
            + Ajouter / Mettre à jour un PR
          </button>
          {/* Liste des PR */}
          {!prs||Object.keys(prs).length===0?(
            <div style={{background:C.card,borderRadius:18,padding:"44px 24px",textAlign:"center",border:`1px dashed ${C.border}`}}>
              
              <p style={{color:C.muted,fontSize:14,lineHeight:1.6}}>Tes records personnels apparaîtront ici.<br/>Un PR = ton poids max sur 1 répétition.</p>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:9}}>
              {Object.entries(prs).sort((a,b)=>b[1].weight-a[1].weight).map(([name,pr])=>{
                const histCount=(pr.history||[]).length+1;
                return(
                <div key={name} onClick={()=>setSelPR({name,pr})}
                  style={{background:C.card,borderRadius:14,padding:"13px 16px",border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"space-between",cursor:"pointer",transition:"border-color 0.15s"}}
                  onMouseEnter={e=>{}}
                  onMouseLeave={e=>{}}>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{color:C.text,fontWeight:700,fontSize:14,margin:"0 0 3px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</p>
                    <p style={{color:C.muted,fontSize:11,margin:0}}>
                      {new Date(pr.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short",year:"numeric"})}
                      {histCount>1&&<span style={{color:C.accent,marginLeft:6}}>· {histCount} entrées</span>}
                    </p>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{textAlign:"right"}}>
                      <p style={{color:C.accent,fontWeight:900,fontSize:20,margin:0}}>{pr.weight} kg</p>
                      <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>PR · 1 REP</p>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5}}>
                      <button onClick={e=>{e.stopPropagation();setPREx(PR_EXERCISES.includes(name)?name:PR_EXERCISES[0]);setPRWeight(pr.weight);setPRRaw(String(pr.weight));setShowPRModal(true);}}
                        style={{background:C.faint,border:"none",borderRadius:9,padding:"7px 10px",color:C.muted,fontSize:13,cursor:"pointer"}}>✎</button>
                      <span style={{color:C.muted,fontSize:14}}>›</span>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
          {/* Modal ajout PR */}
          {showPRModal&&(
            <div style={{position:"fixed",inset:0,zIndex:250,display:"flex",flexDirection:"column",justifyContent:"flex-end"}}>
              {/* Fond semi-transparent cliquable pour fermer */}
              <div onClick={()=>setShowPRModal(false)} style={{position:"absolute",inset:0,background:"rgba(0,0,0,0.55)"}}/>
              {/* Sheet */}
              <div style={{position:"relative",background:C.card,borderRadius:"24px 24px 0 0",padding:"24px 20px",paddingBottom:"calc(env(safe-area-inset-bottom) + 100px)",width:"100%",maxWidth:480,margin:"0 auto",border:`1px solid ${C.border}`,boxSizing:"border-box"}}>
                {/* Poignée */}
                <div style={{width:40,height:4,borderRadius:2,background:C.border,margin:"0 auto 20px"}}/>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
                  <p style={{color:C.accent,fontWeight:800,fontSize:13,letterSpacing:2,margin:0}}>RECORD PERSONNEL</p>
                  <button onClick={()=>setShowPRModal(false)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",lineHeight:1,padding:"4px 8px"}}>×</button>
                </div>
                <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:6}}>EXERCICE</label>
                <select value={prEx} onChange={e=>setPREx(e.target.value)}
                  style={{width:"100%",background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,color:C.text,fontSize:15,padding:"12px 14px",marginBottom:20,outline:"none",boxSizing:"border-box",appearance:"none"}}>
                  {PR_EXERCISES.map(n=><option key={n} value={n}>{n}</option>)}
                </select>
                <label style={{color:C.muted,fontSize:11,letterSpacing:2,display:"block",marginBottom:12}}>POIDS MAX (kg) — 1 REP</label>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:24}}>
                  <button onClick={()=>{const v=Math.max(0,prWeight-2.5);setPRWeight(v);setPRRaw(String(v));}}
                    style={{background:C.faint,border:`1px solid ${C.border}`,borderRadius:12,width:52,height:52,flexShrink:0,color:C.text,fontSize:26,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>−</button>
                  <input type="number" inputMode="decimal" value={prRaw} onChange={e=>setPRRaw(e.target.value)}
                    onBlur={()=>{const v=parseFloat(prRaw);if(!isNaN(v)&&v>=0){setPRWeight(parseFloat(v.toFixed(1)));}else setPRRaw(String(prWeight));}}
                    style={{flex:1,background:C.surface,border:`1.5px solid ${C.accent}`,borderRadius:12,color:C.text,fontSize:32,fontWeight:900,textAlign:"center",padding:"10px 0",outline:"none",minWidth:0}}/>
                  <button onClick={()=>{const v=prWeight+2.5;setPRWeight(v);setPRRaw(String(v));}}
                    style={{background:`${C.accent}18`,border:`1px solid ${C.accent}40`,borderRadius:12,width:52,height:52,flexShrink:0,color:C.accent,fontSize:26,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>+</button>
                </div>
                <button onClick={()=>{if(prEx&&prWeight>0){onSavePR(prEx,{weight:prWeight,date:new Date().toISOString()});setShowPRModal(false);}}}
                  style={{width:"100%",padding:"16px",background:prWeight>0?`linear-gradient(135deg,${C.accent},${C.accent2})`:`${C.faint}`,border:"none",borderRadius:16,color:prWeight>0?"white":C.muted,fontWeight:900,fontSize:16,cursor:prWeight>0?"pointer":"default",letterSpacing:1}}>
                  ENREGISTRER LE PR
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      {tab==="mens"&&(
        <div>
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:14}}>
            <button onClick={()=>setShowMeasModal(true)} style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,padding:"10px 18px",color:"white",fontWeight:800,fontSize:13,cursor:"pointer"}}>+ Ajouter une mesure</button>
          </div>
          {measurements.length===0?(
            <div style={{background:C.card,borderRadius:18,padding:"44px 24px",textAlign:"center",border:`1px dashed ${C.border}`}}>
              
              <p style={{color:C.muted,fontSize:14,lineHeight:1.6}}>Ajoutez votre première mesure pour suivre votre évolution corporelle.</p>
            </div>
          ):(
            <>
            {measurements.filter(m=>m.weight).length>0&&(
              <div onClick={()=>setSelMens({key:"weight",label:"Poids corporel"})}
                style={{background:C.card,borderRadius:14,padding:"12px 14px",border:`1px solid ${C.accent}30`,marginBottom:9,cursor:"pointer",transition:"border-color 0.15s"}}
                onMouseEnter={e=>{}}
                onMouseLeave={e=>{}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <p style={{color:C.muted,fontSize:11,fontWeight:700,letterSpacing:1,margin:0}}>POIDS CORPOREL</p>
                  <span style={{color:C.muted,fontSize:16}}>›</span>
                </div>
                {(()=>{
                  const pts=measurements.filter(m=>m.weight).map(m=>({date:new Date(m.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"}),val:m.weight}));
                  return(
                    <>
                      <LineChart data={pts.slice(-8)} compact={true} height={50} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}/>
                      <p style={{color:C.text,fontWeight:800,fontSize:16,margin:0}}>{pts.at(-1).val} kg <span style={{color:C.muted,fontSize:12,fontWeight:400}}>actuel</span></p>
                    </>
                  );
                })()}
              </div>
            )}
            {MENSURATION_KEYS.map(({k,label})=>{
              const pts=measurements.filter(m=>m[k]).map(m=>({date:new Date(m.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"}),fullDate:m.date,val:parseFloat(m[k])}));
              if(!pts.length)return null;
              const last=pts.at(-1),prev=pts.length>=2?pts.at(-2):null,delta=prev?+(last.val-prev.val).toFixed(1):null;
              const sMax=Math.max(...pts.map(p=>p.val),1);
              return(
                <div key={k} onClick={()=>setSelMens({key:k,label})}
                  style={{background:C.card,borderRadius:14,padding:"12px 14px",border:`1px solid ${C.border}`,marginBottom:9,cursor:"pointer",transition:"border-color 0.15s"}}
                  onMouseEnter={e=>{}}
                  onMouseLeave={e=>{}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:9}}>
                    <div style={{flex:1}}>
                      <p style={{color:C.muted,fontSize:11,fontWeight:700,letterSpacing:1,margin:"0 0 2px"}}>{label.toUpperCase()}</p>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{color:C.text,fontWeight:900,fontSize:18}}>{last.val} cm</span>
                        {delta!==null&&<span style={{color:delta<0?C.green:delta>0?C.red:C.muted,fontWeight:700,fontSize:12}}>{delta>0?`+${delta}`:delta} cm</span>}
                        {pts.length>1&&<span style={{color:C.accent,fontSize:11,marginLeft:4}}>{pts.length} mesures</span>}
                      </div>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                      <div style={{width:60,height:34}}>
                        <LineChart data={pts.slice(-6).map(p=>({val:p.val,date:""}))} compact={true} height={34} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}/>
                      </div>
                      <span style={{color:C.muted,fontSize:16}}>›</span>
                    </div>
                  </div>
                </div>
              );
            })}
            </>
          )}
        </div>
      )}
    </div>
  );
}


function SessionSummaryPage({session,streak,onClose,onShare,userWeight,xpGained=0,onLaunchFinisher,onAiAnalysis,onApplySuggestions,fitnessGoal=null,prevSessions=[],freeAnalysesUsed=0,lastFreeAnalysisDate=null,onFreeAnalysisUsed,C}) {
  // ⚠️ Hooks MUST be called before any early return (Rules of Hooks)
  const [copied,setCopied]=useState(false);
  const [note,setNote]=useState(session?.note||"");
  const [generating,setGenerating]=useState(false);
  const [showFaq,setShowFaq]=useState(false);
  const [showFinisherModal,setShowFinisherModal]=useState(false);
  const [aiAnalysis,setAiAnalysis]=useState(session?.aiAnalysis||null);
  const [aiLoading,setAiLoading]=useState(false);
  const [showAiModal,setShowAiModal]=useState(false);
  const [aiApplied,setAiApplied]=useState(session?.aiApplied||false);
  const [analysisError,setAnalysisError]=useState(false);
  const [selectedAdaptations,setSelectedAdaptations]=useState(null); // null = toutes cochées par défaut

  // TODO: remplacer par isPremium réel quand Stripe sera branché
  const isPremium = true; // TODO: remplacer par isPremium réel quand RevenueCat sera branché

  // Logique analyses gratuites (utilisée quand isPremium===false)
  const FREE_ANALYSIS_MAX = 3;
  const FREE_ANALYSIS_DELAY_DAYS = 7;
  const analysesExhausted = freeAnalysesUsed >= FREE_ANALYSIS_MAX;
  const lastDate = lastFreeAnalysisDate ? new Date(lastFreeAnalysisDate) : null;
  const daysSinceLast = lastDate ? (Date.now() - lastDate.getTime()) / (1000*60*60*24) : Infinity;
  const analysisOnCooldown = !analysesExhausted && daysSinceLast < FREE_ANALYSIS_DELAY_DAYS;
  const nextAvailableDate = analysisOnCooldown && lastDate
    ? new Date(lastDate.getTime() + FREE_ANALYSIS_DELAY_DAYS*24*60*60*1000).toLocaleDateString("fr-FR",{day:"numeric",month:"long"})
    : null;
  const canAnalyzeFree = !analysesExhausted && !analysisOnCooldown;

  const handleAiAnalysis = async () => {
    if(aiLoading||aiAnalysis) return;
    setAnalysisError(false);
    setAiLoading(true);
    try {
      const exs = session.exercises||[];
      const vol = exs.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,s)=>b+(s.reps||0)*(s.weight||0),0),0);

      // Détection stagnation : même charge sur les 3 dernières séances pour chaque exercice
      const stagnations = [];
      exs.forEach(ex => {
        const history = prevSessions
          .filter(ps => (ps.exercises||[]).some(e=>e.name===ex.name))
          .slice(-3)
          .map(ps => {
            const found = ps.exercises.find(e=>e.name===ex.name);
            const maxW = found ? Math.max(...(found.sets||[]).map(s=>s.weight||0)) : 0;
            return maxW;
          });
        if(history.length>=3 && history.every(w=>w>0&&w===history[0])) {
          const alts = (typeof EXERCISE_DATABASE !== "undefined" && EXERCISE_DATABASE[ex.name]?.alternatives)||[];
          const altNames = alts.map(a=>a.name).filter(n=>n&&n!==ex.name);
          stagnations.push({exercise:ex.name, alternatives:altNames});
        }
      });

      const stagnationBlock = stagnations.length>0
        ? `\nStagnations détectées :\n${stagnations.map(st=>`- ${st.exercise}${st.alternatives.length?` (alternatives : ${st.alternatives.join(", ")})`:"" }`).join("\n")}`
        : "";

      // Séances précédentes du même programme (max 2)
      const prevSameProgram = prevSessions
        .filter(ps => ps.programId === session.programId || ps.programName === session.programName)
        .slice(-2);

      const formatSessionDetail = (s) => {
        const sExs = s.exercises||[];
        return sExs.map(ex => {
          const seriesDetail = (ex.sets||[]).map(st => `${st.weight||0}kg×${st.reps||0}`).join(" / ");
          return `  - ${ex.name} : ${seriesDetail}`;
        }).join("\n");
      };

      const prevBlock = prevSameProgram.length > 0
        ? `\n\nHistorique (${prevSameProgram.length} séance${prevSameProgram.length>1?"s":""} précédente${prevSameProgram.length>1?"s":""}) :\n` +
          prevSameProgram.map((ps, i) => {
            const d = ps.date ? new Date(ps.date).toLocaleDateString("fr-FR") : `J-${prevSameProgram.length - i}`;
            return `Séance du ${d} :\n${formatSessionDetail(ps)}`;
          }).join("\n\n")
        : "\n\nPremière séance enregistrée pour ce programme — pas d'historique disponible.";

      const userPrompt = `Séance : ${session.programName}
Durée : ${session.duration} min
Volume total : ${vol} kg
Objectif utilisateur : ${fitnessGoal||"hypertrophie"}

Exercices (détail série par série) :
${exs.map(ex=>{
  const seriesDetail = (ex.sets||[]).map(st => `${st.weight||0}kg×${st.reps||0}`).join(" / ");
  const plannedSets = ex.plannedSets ? ` [prévu : ${ex.plannedSets}×${ex.plannedReps||"?"} reps]` : "";
  return `- ${ex.name}${plannedSets} : ${seriesDetail}`;
}).join("\n")}${stagnationBlock}${prevBlock}`;

      const response = await fetch("/api/beaggy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userPrompt, stagnations, fitnessGoal }),
      });
      const data = await response.json();
      const result = data.analysis || data.text || "Analyse indisponible.";
      setAiAnalysis(result);
      setShowAiModal(true);
      if(onAiAnalysis) onAiAnalysis(session.id, result);
      if(!isPremium && onFreeAnalysisUsed) onFreeAnalysisUsed();
    } catch(e) {
      setAnalysisError(true);
    }
    setAiLoading(false);
  };

  if(!session) return null;
  const isTabata=session.type==="tabata";
  const exs=session.exercises||[];
  const vol=isTabata?0:exs.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,s)=>b+(s.reps||0)*(s.weight||0),0),0);
  const totalSets=isTabata?0:exs.reduce((a,ex)=>a+(ex.sets||[]).length,0);
  const totalReps=isTabata?0:exs.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,s)=>b+(s.reps||0),0),0);
  const muscles=isTabata?[]:([...new Set(exs.map(e=>e.muscle).filter(Boolean))]);

  // Calcul calories estimées (MET × poids × durée en heures)
  const computeKcal = () => {
    const w = userWeight && userWeight > 0 ? userWeight : null;
    if (!w || !session.duration || session.duration <= 0) return null;
    const hours = session.duration / 60;
    let met;
    if (isTabata) {
      met = 8;
    } else {
      // Détecter l'intensité selon le volume moyen par série
      const avgWeight = totalSets > 0 ? vol / totalSets : 0;
      if (avgWeight >= 60) met = 6;
      else if (avgWeight >= 20) met = 5;
      else met = 3.5;
    }
    return Math.round(met * w * hours);
  };
  const kcal = computeKcal();

  const handleShare=()=>{if(onShare)onShare();};

  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,zIndex:400,display:"flex",flexDirection:"column",overflow:"hidden"}}>

      {/* Header compact */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"calc(env(safe-area-inset-top) + 20px) 20px 16px",background:`linear-gradient(180deg,${C.accent}14,transparent)`}}>
        <img src={LOGO_LIGHT_DATA} alt="Calestan" style={{height:36,objectFit:"contain"}}/>
        <div style={{textAlign:"right"}}>
          <p style={{color:C.accent,fontSize:9,letterSpacing:3,margin:0,fontWeight:700}}>SÉANCE TERMINÉE</p>
          <p style={{color:C.muted,fontSize:11,margin:0}}>{new Date(session.date).toLocaleDateString("fr-FR",{weekday:"short",day:"numeric",month:"short"})}</p>
        </div>
      </div>

      {/* Contenu principal — scrollable */}
      <div style={{flex:1,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"0 20px 24px"}}>

        {/* Nom séance */}
        <div style={{textAlign:"center",marginBottom:14}}>
          <h2 style={{color:C.text,fontSize:20,fontWeight:900,margin:"0 0 2px",lineHeight:1.2}}>{session.programName}</h2>
          {streak>0&&<p style={{color:C.accent,fontSize:12,fontWeight:700,margin:"0 0 6px"}}>🔥 {streak} semaine{streak>1?"s":""} d'affilée</p>}
          {xpGained>0&&(
            <div style={{display:"inline-flex",alignItems:"center",gap:6,background:`${C.accent}15`,border:`1px solid ${C.accent}40`,borderRadius:20,padding:"5px 14px",marginTop:streak>0?0:4}}>
              <span style={{color:C.accent,fontSize:15,fontWeight:900}}>+{xpGained}</span>
              <span style={{color:C.accent,fontSize:11,fontWeight:700,letterSpacing:1}}>XP</span>
            </div>
          )}
        </div>

        {/* Stats — ligne unique 4 colonnes (muscu) ou 3 colonnes (tabata) */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:6,marginBottom:10}}>
          {(isTabata?[
            {label:"ROUNDS",v:session.rounds},
            {label:"EXERCICES",v:session.totalEx},
            {label:"DURÉE",v:`${session.duration}min`},
            {label:"KCAL",v:kcal?`~${kcal}`:"—"},
          ]:[
            {label:"DURÉE",v:`${session.duration}min`},
            {label:"VOLUME",v:vol>=1000?`${(vol/1000).toFixed(1)}T`:`${vol}kg`},
            {label:"SÉRIES",v:totalSets},
            {label:kcal?"KCAL":"REPS",v:kcal?`~${kcal}`:totalReps},
          ]).map(s=>(
            <div key={s.label} style={{background:C.card,borderRadius:12,padding:"10px 6px",textAlign:"center",border:`1px solid ${C.border}`}}>
              <p style={{color:s.v==="—"?C.muted:C.accent,fontSize:18,fontWeight:900,margin:"0 0 2px",lineHeight:1}}>{s.v}</p>
              <p style={{color:C.muted,fontSize:8,letterSpacing:1.5,margin:0}}>{s.label}</p>
            </div>
          ))}
        </div>

        {/* Mention poids non renseigné */}
        {!kcal&&(
          <p style={{color:C.muted,fontSize:11,margin:"0 0 10px",textAlign:"center"}}>
            Renseigne ton poids dans le profil pour calculer les kcal
          </p>
        )}

        {/* Muscles sollicités */}
        {muscles.length>0&&(
          <div style={{marginBottom:12}}>
            <p style={{color:C.muted,fontSize:9,letterSpacing:2,fontWeight:700,margin:"0 0 6px",textTransform:"uppercase"}}>Muscles sollicités</p>
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {muscles.map(m=><Tag key={m} label={m} color={MM[m]?.color||C.muted} bg={MM[m]?.bg||C.faint}/>)}
            </div>
          </div>
        )}

        {/* Note — 2 lignes */}
        <div style={{marginBottom:14}}>
          <textarea value={note} onChange={e=>setNote(e.target.value)}
            placeholder="Notes : sensations, fatigue, PR..."
            style={{width:"100%",height:68,background:C.card,border:`1px solid ${note?C.accent+"50":C.border}`,borderRadius:12,color:C.text,fontSize:14,padding:"10px 12px",outline:"none",boxSizing:"border-box",resize:"none",fontFamily:"inherit",lineHeight:1.5}}/>
        </div>

      </div>

      {/* Modal choix finisher */}
      {showFinisherModal&&(()=>{
          // Mapping muscle muscu → muscle Tabata
          const MUSCLE_MAP = {
            "Pectoraux": "Pectoraux", "Triceps": "Triceps", "Épaules": "Cardio / Full body",
            "Dos": "Cardio / Full body", "Biceps": "Cardio / Full body",
            "Quadriceps": "Quadriceps", "Ischio-jambiers": "Fessiers",
            "Fessiers": "Fessiers", "Mollets": "Cardio / Full body",
            "Abdominaux": "Abdominaux", "Trapèzes": "Cardio / Full body",
          };
          const primaryMuscle = muscles[0] || "";
          const tabataMuscle = MUSCLE_MAP[primaryMuscle] || "Cardio / Full body";

          // Muscles avec finisher musculaire dédié (pecs + jambes)
          const MUSCLE_SPECIFIC = ["Pectoraux","Quadriceps","Ischio-jambiers","Fessiers","Mollets"];
          const hasMuscleFinisher = MUSCLE_SPECIFIC.includes(primaryMuscle);

          const buildFinisher = (type) => {
            let pool;
            if (type === "cardio") pool = TABATA_EX_BASE.filter(e => e.muscle === "Cardio / Full body");
            else if (type === "abdos") pool = TABATA_EX_BASE.filter(e => e.muscle === "Abdominaux");
            else if (!hasMuscleFinisher) {
              // Pas de finisher musculaire dédié → alternance abdos + cardio
              const abdosPool = [...TABATA_EX_BASE.filter(e => e.muscle === "Abdominaux")].sort(() => Math.random() - 0.5);
              const cardioPool = [...TABATA_EX_BASE.filter(e => e.muscle === "Cardio / Full body")].sort(() => Math.random() - 0.5);
              // Alterner : abdos, cardio, abdos, cardio, abdos, cardio
              const mixed = [];
              for (let i = 0; i < 3; i++) {
                if (abdosPool[i]) mixed.push(abdosPool[i]);
                if (cardioPool[i]) mixed.push(cardioPool[i]);
              }
              const label = `Finisher ${primaryMuscle}`;
              return { id: Date.now(), name: label, workTime: 20, restTime: 10, rounds: 2, exercises: mixed.map(e => ({...e})) };
            } else {
              pool = TABATA_EX_BASE.filter(e => e.muscle === tabataMuscle && e.muscle !== "Cardio / Full body");
              // Fallback si pas assez d'exos pour le finisher musculaire
              if (pool.length < 4) pool = TABATA_EX_BASE.filter(e => e.muscle === "Cardio / Full body");
            }

            // Mélange et sélection de 6 exos
            const shuffled = [...pool].sort(() => Math.random() - 0.5).slice(0, 6);
            const label = type === "cardio" ? "Finisher Cardio" : type === "abdos" ? "Finisher Abdos" : `Finisher ${primaryMuscle}`;
            return {
              id: Date.now(),
              name: label,
              workTime: 20,
              restTime: 10,
              rounds: 2,
              exercises: shuffled.map(e => ({...e})),
            };
          };

          const muscleFinisherSub = hasMuscleFinisher
            ? `Cible le muscle travaillé · 2 rounds · 20s/10s`
            : `Abdos + Cardio · 2 rounds · 20s/10s`;

          const TYPES = [
            {id:"cardio", label:"Cardio", sub:"Full body · 2 rounds · 20s/10s"},
            {id:"abdos",  label:"Abdos",  sub:"Gainage & abdos · 2 rounds · 20s/10s"},
            {id:"muscle", label:`Finisher ${primaryMuscle||"Musculaire"}`, sub:muscleFinisherSub, premium:true},
          ];

          return (
            <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.95)",backdropFilter:"blur(12px)",display:"flex",alignItems:"flex-end",justifyContent:"center",zIndex:500}} onClick={()=>setShowFinisherModal(false)}>
              <div style={{background:C.card,borderRadius:"20px 20px 0 0",padding:"24px 20px calc(env(safe-area-inset-bottom) + 24px)",width:"100%",maxWidth:480,border:`1px solid ${C.border}`}} onClick={e=>e.stopPropagation()}>
                <p style={{color:C.text,fontWeight:900,fontSize:16,margin:"0 0 4px"}}>Finisher Tabata</p>
                <p style={{color:C.muted,fontSize:12,margin:"0 0 20px"}}>Choisir le type de finisher</p>
                <div style={{display:"flex",flexDirection:"column",gap:10}}>
                  {TYPES.map(t=>{
                    const locked = t.premium && !isPremium;
                    return (
                      <button key={t.id} onClick={()=>{
                        if(locked) return;
                        setShowFinisherModal(false);
                        onLaunchFinisher(buildFinisher(t.id));
                      }}
                        style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"14px 16px",background:locked?C.faint:C.surface,border:`1px solid ${locked?C.border:C.border}`,borderRadius:14,cursor:locked?"default":"pointer",textAlign:"left",opacity:locked?0.7:1}}>
                        <div>
                          <p style={{color:locked?C.muted:C.text,fontWeight:800,fontSize:14,margin:"0 0 2px"}}>
                            {t.label}
                            {t.premium&&locked&&<span style={{marginLeft:6,fontSize:10,color:C.accent,fontWeight:700,letterSpacing:1}}>PREMIUM</span>}
                          </p>
                          <p style={{color:C.muted,fontSize:11,margin:0}}>{locked?"Disponible avec Calestan Premium":t.sub}</p>
                        </div>
                        {locked?(
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                          </svg>
                        ):(
                          <span style={{color:C.muted,fontSize:18}}>›</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <button onClick={()=>setShowFinisherModal(false)} style={{width:"100%",marginTop:12,padding:"12px",background:"none",border:"none",color:C.muted,fontWeight:700,fontSize:14,cursor:"pointer"}}>Annuler</button>
              </div>
            </div>
          );
        })()}

      {/* Modal plein écran analyse IA */}
      {showAiModal&&aiAnalysis&&(
        <div style={{position:"fixed",inset:0,zIndex:600,background:C.bg,display:"flex",flexDirection:"column"}}>
          {/* Header */}
          <div style={{flexShrink:0,padding:`calc(env(safe-area-inset-top) + 16px) 20px 12px`,borderBottom:`1px solid ${C.border}`}}>
            <p style={{color:C.accent,fontSize:9,letterSpacing:2,fontWeight:700,margin:"0 0 2px"}}>ANALYSE BEAGGY IA ✦</p>
            <p style={{color:C.muted,fontSize:11,margin:0}}>{session.programName}</p>
          </div>
          {/* Contenu scrollable */}
          <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
            {(()=>{
              const sections=[
                {key:"bilan",label:"Bilan"},
                {key:"pointFort",label:"Point fort"},
                {key:"suggestion",label:"Prochaine séance"},
                {key:"variante",label:"Variante"},
              ];
              if(typeof aiAnalysis==="object"&&aiAnalysis!==null){
                return(<>
                  {sections.filter(s=>aiAnalysis[s.key]&&aiAnalysis[s.key]!==null).map((s,i)=>(
                    <div key={s.key} style={{marginTop:i===0?0:14}}>
                      <p style={{color:C.accent,fontSize:11,fontWeight:700,margin:"0 0 4px"}}>· {s.label}</p>
                      <p style={{color:C.text,fontSize:14,lineHeight:1.6,margin:0,paddingLeft:10}}>{aiAnalysis[s.key]}</p>
                    </div>
                  ))}
                  {aiAnalysis.adaptations&&aiAnalysis.adaptations.length>0&&(
                    <div style={{marginTop:20}}>
                      <p style={{color:C.accent,fontSize:9,letterSpacing:2,fontWeight:700,margin:"0 0 4px"}}>SUGGESTIONS BEAGGY IA POUR LA PROCHAINE SÉANCE</p>
                      <p style={{color:C.muted,fontSize:11,margin:"0 0 10px"}}>Sélectionne les exercices à appliquer</p>
                      {aiAnalysis.adaptations.map((a,i)=>{
                        const sel = selectedAdaptations===null ? true : (selectedAdaptations[i]!==false);
                        return(
                          <div key={i} onClick={()=>{
                            const base = selectedAdaptations===null
                              ? Object.fromEntries(aiAnalysis.adaptations.map((_,j)=>[j,true]))
                              : {...selectedAdaptations};
                            base[i]=!sel;
                            setSelectedAdaptations(base);
                          }} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"9px 12px",background:sel?`${C.accent}10`:C.card,borderRadius:10,marginBottom:6,border:`1.5px solid ${sel?C.accent:C.border}`,cursor:"pointer",userSelect:"none"}}>
                            <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                              <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${sel?C.accent:C.muted}`,background:sel?C.accent:"none",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center"}}>
                                {sel&&<span style={{color:"white",fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{color:C.text,fontSize:13,fontWeight:700,margin:"0 0 2px",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{a.exercise}</p>
                                <p style={{color:C.muted,fontSize:11,margin:0}}>{a.reason}</p>
                              </div>
                            </div>
                            <div style={{flexShrink:0,textAlign:"right",marginLeft:12}}>
                              <p style={{color:sel?C.accent:C.muted,fontSize:13,fontWeight:800,margin:0}}>{a.sets}×{a.reps} reps</p>
                              <p style={{color:C.muted,fontSize:11,margin:0}}>{a.weight>0?`${a.weight} kg`:"poids libre"}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>);
              }
              return <p style={{color:C.text,fontSize:14,lineHeight:1.6,margin:0}}>{String(aiAnalysis)}</p>;
            })()}
          </div>
          {/* Boutons fixes en bas */}
          <div style={{flexShrink:0,padding:`12px 20px calc(env(safe-area-inset-bottom) + 16px)`,borderTop:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:8}}>
            {aiAnalysis?.adaptations?.length>0&&!aiApplied&&(()=>{
              const selected = aiAnalysis.adaptations.filter((_,i)=>
                selectedAdaptations===null ? true : (selectedAdaptations[i]!==false)
              );
              const count = selected.length;
              return count > 0 ? (
                <button onClick={()=>{
                  if(onApplySuggestions) onApplySuggestions(session.id, selected);
                  setAiApplied(true);
                  setShowAiModal(false);
                }}
                  style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:13,color:"white",fontWeight:800,fontSize:14,cursor:"pointer",boxShadow:`0 4px 16px ${C.glow}`}}>
                  Appliquer {count === aiAnalysis.adaptations.length ? "toutes les suggestions" : `${count} suggestion${count>1?"s":""} sélectionnée${count>1?"s":""}`}
                </button>
              ) : (
                <button disabled style={{width:"100%",padding:"14px",background:C.faint,border:"none",borderRadius:13,color:C.muted,fontWeight:800,fontSize:14,cursor:"not-allowed"}}>
                  Aucune suggestion sélectionnée
                </button>
              );
            })()}
            {aiApplied&&(
              <p style={{textAlign:"center",color:C.muted,fontSize:12,margin:0}}>✓ Suggestions appliquées</p>
            )}
            <button onClick={()=>setShowAiModal(false)}
              style={{width:"100%",padding:"14px",background:"none",border:`1px solid ${C.border}`,borderRadius:13,color:C.muted,fontWeight:700,fontSize:14,cursor:"pointer"}}>
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Boutons fixes en bas */}
      <div style={{flexShrink:0,padding:`12px 20px calc(env(safe-area-inset-bottom) + 12px)`,background:C.bg,borderTop:`1px solid ${C.border}`,display:"flex",flexDirection:"column",gap:8,position:"relative",zIndex:1}}>

        {/* Bloc analyse IA */}
        {!isTabata&&(
          isPremium ? (
            analysisError ? (
              <div style={{width:"100%",padding:"13px",background:C.card,border:`1px solid #ef444450`,borderRadius:13,display:"flex",flexDirection:"column",gap:8}}>
                <p style={{margin:0,color:"#ef4444",fontWeight:700,fontSize:13}}>⚠️ IA indisponible pour le moment — réessaie plus tard.{!navigator.onLine?" Vérifie ta connexion internet.":""}</p>
                <button onClick={()=>{setAnalysisError(false);handleAiAnalysis();}}
                  style={{padding:"8px 14px",background:"none",border:`1px solid #ef444470`,borderRadius:9,color:"#ef4444",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  🔄 Réessayer
                </button>
              </div>
            ) : aiAnalysis ? (
              <button onClick={()=>setShowAiModal(true)}
                style={{width:"100%",padding:"13px",background:C.card,border:`1px solid ${C.accent}40`,borderRadius:13,color:C.accent,fontWeight:800,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span>✦ Analyse Beaggy IA</span>
                <span style={{fontSize:12,fontWeight:600}}>{aiApplied?"✓ Appliqué":"Voir →"}</span>
              </button>
            ) : (
              <button onClick={handleAiAnalysis} disabled={aiLoading}
                style={{width:"100%",padding:"13px",background:aiLoading?C.faint:C.card,border:`1px solid ${aiLoading?C.border:C.accent+"60"}`,borderRadius:13,color:aiLoading?C.muted:C.accent,fontWeight:800,fontSize:14,cursor:aiLoading?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                {aiLoading?(
                  <><span style={{display:"inline-block",width:14,height:14,border:`2px solid ${C.accent}40`,borderTop:`2px solid ${C.accent}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>Beaggy analyse ta séance...</>
                ):"✦ Analyser ma séance avec Beaggy IA"}
              </button>
            )
          ) : aiAnalysis ? (
            <button onClick={()=>setShowAiModal(true)}
              style={{width:"100%",padding:"13px",background:C.card,border:`1px solid ${C.accent}40`,borderRadius:13,color:C.accent,fontWeight:800,fontSize:14,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <span>✦ Analyse Beaggy IA</span>
              <span style={{fontSize:12,fontWeight:600}}>{aiApplied?"✓ Appliqué":"Voir →"}</span>
            </button>
          ) : analysisError ? (
            <div style={{width:"100%",padding:"13px",background:C.card,border:`1px solid #ef444450`,borderRadius:13,display:"flex",flexDirection:"column",gap:8}}>
              <p style={{margin:0,color:"#ef4444",fontWeight:700,fontSize:13}}>⚠️ IA indisponible pour le moment — réessaie plus tard.{!navigator.onLine?" Vérifie ta connexion internet.":""}</p>
              <button onClick={()=>{setAnalysisError(false);handleAiAnalysis();}}
                style={{padding:"8px 14px",background:"none",border:`1px solid #ef444470`,borderRadius:9,color:"#ef4444",fontWeight:700,fontSize:13,cursor:"pointer"}}>
                🔄 Réessayer
              </button>
            </div>
          ) : canAnalyzeFree ? (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button onClick={handleAiAnalysis} disabled={aiLoading}
                style={{width:"100%",padding:"13px",background:aiLoading?C.faint:C.card,border:`1px solid ${aiLoading?C.border:C.accent+"60"}`,borderRadius:13,color:aiLoading?C.muted:C.accent,fontWeight:800,fontSize:14,cursor:aiLoading?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                {aiLoading?(
                  <><span style={{display:"inline-block",width:14,height:14,border:`2px solid ${C.accent}40`,borderTop:`2px solid ${C.accent}`,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>Beaggy analyse ta séance...</>
                ):"✦ Analyser ma séance avec Beaggy IA"}
              </button>
              <p style={{margin:0,textAlign:"center",fontSize:11,color:C.muted,fontWeight:600}}>{freeAnalysesUsed}/{FREE_ANALYSIS_MAX} analyses gratuites utilisées</p>
            </div>
          ) : analysisOnCooldown ? (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button disabled style={{width:"100%",padding:"13px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:13,color:C.muted,fontWeight:800,fontSize:13,cursor:"default",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,opacity:0.7}}>
                <span style={{flex:1,textAlign:"left"}}>✦ Analyse Beaggy IA</span>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </button>
              <p style={{margin:0,textAlign:"center",fontSize:11,color:C.muted,fontWeight:600}}>{freeAnalysesUsed}/{FREE_ANALYSIS_MAX} analyses gratuites — prochaine disponible le {nextAvailableDate}</p>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              <button disabled style={{width:"100%",padding:"13px",background:C.faint,border:`1px solid ${C.border}`,borderRadius:13,color:C.muted,fontWeight:800,fontSize:13,cursor:"default",display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,opacity:0.7}}>
                <span style={{flex:1,textAlign:"left"}}>✦ Analyse Beaggy IA</span>
                <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
                  <span style={{fontSize:10,color:C.accent,fontWeight:700,letterSpacing:1}}>PREMIUM</span>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
              </button>
              <p style={{margin:0,textAlign:"center",fontSize:11,color:C.muted,fontWeight:600}}>3/3 analyses gratuites utilisées — <span style={{color:C.accent,cursor:"pointer",fontWeight:700}}>Passer Premium →</span></p>
            </div>
          )
        )}
        {!isTabata&&onLaunchFinisher&&(
          <button onClick={()=>setShowFinisherModal(true)}
            style={{width:"100%",padding:"13px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:13,color:"white",fontWeight:800,fontSize:14,cursor:"pointer",boxShadow:`0 4px 16px ${C.glow}`}}>
            Lancer un finisher Tabata
          </button>
        )}
        <button onClick={handleShare} disabled={generating}
          style={{width:"100%",padding:"13px",background:generating?C.faint:C.card,border:`1px solid ${C.border}`,borderRadius:13,color:generating?C.muted:C.text,fontWeight:800,fontSize:14,cursor:generating?"default":"pointer",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
          {generating?"Génération...":"↑ Partager"}
        </button>
        <button onClick={()=>onClose(note)}
          style={{width:"100%",padding:"13px",background:"none",border:"none",borderRadius:13,color:C.muted,fontWeight:700,fontSize:14,cursor:"pointer"}}>
          {note?"Enregistrer ✓":"Fermer"}
        </button>
      </div>
    </div>
  );
}


// ─── HISTORIQUE (muscu + tabata) ──────────────────────────────────────────────
function HistoryPage({sessions,onDeleteSession,onBack,C}) {
  const [sel,setSel]=useState(null);
  const [confirmDel,setConfirmDel]=useState(false);
  const [calMonth,setCalMonth]=useState(new Date());
  const [selectedDate,setSelectedDate]=useState(null);
  const sessionDates=new Set(sessions.map(s=>new Date(s.date).toDateString()));
  const yr=calMonth.getFullYear(),mo=calMonth.getMonth();
  const firstDay=new Date(yr,mo,1).getDay();
  const daysInMonth=new Date(yr,mo+1,0).getDate();
  const startOffset=(firstDay+6)%7;
  const today=new Date();
  const dayNames=["Lu","Ma","Me","Je","Ve","Sa","Di"];
  const monthName=calMonth.toLocaleDateString("fr-FR",{month:"long",year:"numeric"});

  if(sel){
    const isTabata=sel.type==="tabata";
    if(isTabata){
      return(
        <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
          <div style={{padding:"0 0 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <button onClick={()=>setSel(null)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
              <div>
                <h2 style={{color:C.text,fontSize:18,fontWeight:900,margin:0}}>{sel.programName}</h2>
                <p style={{color:C.muted,fontSize:12,margin:0}}>{new Date(sel.date).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</p>
              </div>
            </div>
            <button onClick={()=>setConfirmDel(true)} style={{background:"rgba(239,68,68,0.08)",border:"none",borderRadius:10,padding:"8px 12px",color:C.red,fontWeight:700,fontSize:13,cursor:"pointer",flexShrink:0}}>Supprimer</button>
          </div>
          {confirmDel&&<ConfirmModal message="Supprimer cette séance définitivement ?" onConfirm={()=>{onDeleteSession(sel.id);setSel(null);setConfirmDel(false);}} onCancel={()=>setConfirmDel(false)} C={C}/>}
          <div style={{background:`${C.accent}0F`,border:`1px solid ${C.accent}25`,borderRadius:14,padding:"12px 16px",marginBottom:14,display:"inline-flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}></span>
            <span style={{color:C.accent,fontWeight:700,fontSize:14}}>Séance Tabata</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
            {[{l:"ROUNDS",v:sel.rounds},{l:"EXERCICES",v:sel.totalEx},{l:"INTERVALLE",v:sel.interval||"20-10"}].map(s=>(
              <div key={s.l} style={{background:C.card,borderRadius:13,padding:"14px 10px",textAlign:"center",border:`1px solid ${C.border}`}}>
                <p style={{color:C.accent,fontSize:15,fontWeight:900,margin:"0 0 2px"}}>{s.v}</p>
                <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>{s.l}</p>
              </div>
            ))}
          </div>
          {sel.exercises&&sel.exercises.length>0&&(
            <div style={{background:C.card,borderRadius:14,padding:"14px 16px",border:`1px solid ${C.border}`}}>
              <p style={{color:C.muted,fontSize:10,letterSpacing:2,fontWeight:700,margin:"0 0 10px"}}>COMPOSITION</p>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {sel.exercises.map((e,i)=>{
                  const meta=MM[e.muscle]||{};
                  return(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{color:C.muted,fontSize:11,fontWeight:700,minWidth:18,textAlign:"right"}}>{i+1}.</span>
                      <span style={{background:meta.bg||C.faint,color:meta.color||C.muted,fontSize:12,padding:"4px 10px",borderRadius:10,fontWeight:600}}>{e.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }
    const vol=sel.exercises.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,s)=>b+s.reps*s.weight,0),0);
    const reps=sel.exercises.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,s)=>b+s.reps,0),0);
    return(
      <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
        <div style={{padding:"0 0 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button onClick={()=>setSel(null)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
            <div><h2 style={{color:C.text,fontSize:18,fontWeight:900,margin:0}}>{sel.programName}</h2><p style={{color:C.muted,fontSize:12,margin:0}}>{new Date(sel.date).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</p></div>
          </div>
          <button onClick={()=>setConfirmDel(true)} style={{background:"rgba(239,68,68,0.08)",border:"none",borderRadius:10,padding:"8px 12px",color:C.red,fontWeight:700,fontSize:13,cursor:"pointer",flexShrink:0}}>Supprimer</button>
        </div>
        {confirmDel&&<ConfirmModal message="Supprimer cette séance définitivement ?" onConfirm={()=>{onDeleteSession(sel.id);setSel(null);setConfirmDel(false);}} onCancel={()=>setConfirmDel(false)} C={C}/>}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:16}}>
          {[{l:"DURÉE",v:`${sel.duration}min`},{l:"REPS",v:reps},{l:"VOLUME",v:`${vol.toLocaleString()}kg`}].map(s=>(
            <div key={s.l} style={{background:C.card,borderRadius:13,padding:14,textAlign:"center",border:`1px solid ${C.border}`}}>
              <p style={{color:C.accent,fontSize:16,fontWeight:900,margin:"0 0 2px"}}>{s.v}</p>
              <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>{s.l}</p>
            </div>
          ))}
        </div>
        {sel.note&&(
          <div style={{background:C.card,borderRadius:14,padding:"12px 16px",border:`1px solid ${C.border}`,marginBottom:12}}>
            <p style={{color:C.muted,fontSize:10,letterSpacing:2,margin:"0 0 6px"}}>NOTES DE SÉANCE</p>
            <p style={{color:C.text,fontSize:13,lineHeight:1.6,margin:0,whiteSpace:"pre-wrap"}}>{sel.note}</p>
          </div>
        )}
        {sel.aiAnalysis&&typeof sel.aiAnalysis==="object"&&(sel.aiAnalysis.bilan||sel.aiAnalysis.pointFort||sel.aiAnalysis.suggestion)&&(
          <div style={{background:`${C.accent}0D`,border:`1px solid ${C.accent}30`,borderRadius:14,padding:"14px 16px",marginBottom:12}}>
            <p style={{color:C.accent,fontSize:10,letterSpacing:2,fontWeight:700,margin:"0 0 10px"}}>✦ ANALYSE BEAGGY IA</p>
            {sel.aiAnalysis.bilan&&(
              <p style={{color:C.text,fontSize:13,lineHeight:1.6,margin:"0 0 8px"}}>{sel.aiAnalysis.bilan}</p>
            )}
            {sel.aiAnalysis.pointFort&&(
              <div style={{background:`${C.accent}15`,borderRadius:10,padding:"8px 12px",marginBottom:8}}>
                <p style={{color:C.muted,fontSize:10,letterSpacing:1,fontWeight:700,margin:"0 0 3px"}}>POINT FORT</p>
                <p style={{color:C.text,fontSize:13,lineHeight:1.5,margin:0}}>{sel.aiAnalysis.pointFort}</p>
              </div>
            )}
            {sel.aiAnalysis.suggestion&&(
              <div style={{background:C.faint,borderRadius:10,padding:"8px 12px"}}>
                <p style={{color:C.muted,fontSize:10,letterSpacing:1,fontWeight:700,margin:"0 0 3px"}}>CONSEIL</p>
                <p style={{color:C.text,fontSize:13,lineHeight:1.5,margin:0}}>{sel.aiAnalysis.suggestion}</p>
              </div>
            )}
          </div>
        )}
        {sel.exercises.map((ex,i)=>(ex.sets||[]).length>0&&(
          <div key={i} style={{background:C.card,borderRadius:13,padding:"12px 14px",border:`1px solid ${C.border}`,marginBottom:8}}>
            <p style={{color:C.text,fontWeight:700,margin:"0 0 4px",fontSize:14}}>{ex.name}</p>
            {ex.detail&&<p style={{color:C.muted,fontSize:11,fontStyle:"italic",margin:"0 0 8px"}}>{ex.detail}</p>}
            {ex.sets.map((s,j)=>(
              <div key={j} style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{background:C.faint,color:C.muted,width:20,height:20,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700}}>{j+1}</span>
                <span style={{color:C.text,fontWeight:600,fontSize:13}}>{s.reps} reps ×</span>
                <span style={{color:C.accent,fontWeight:700,fontSize:13}}>{s.weight} kg</span>
                <span style={{color:C.muted,fontSize:11,marginLeft:"auto"}}>{(s.reps*s.weight).toLocaleString()} kg</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
      <div style={{padding:"0 0 18px",display:"flex",justifyContent:"space-between",alignItems:"flex-end"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {onBack&&<button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:"0 4px 0 0",flexShrink:0}}>←</button>}
          <div>
            <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 4px"}}>MES SÉANCES</p>
            <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>Calendrier</h1>
          </div>
        </div>
      </div>
      <div style={{background:C.card,borderRadius:18,padding:"16px",border:`1px solid ${C.border}`,marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
          <button onClick={()=>setCalMonth(d=>new Date(d.getFullYear(),d.getMonth()-1,1))} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",padding:"4px 8px"}}>‹</button>
          <p style={{color:C.text,fontWeight:700,fontSize:14,margin:0,textTransform:"capitalize"}}>{monthName}</p>
          <button onClick={()=>setCalMonth(d=>new Date(d.getFullYear(),d.getMonth()+1,1))} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",padding:"4px 8px"}}>›</button>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4,marginBottom:6}}>
          {dayNames.map(d=><span key={d} style={{color:C.muted,fontSize:10,fontWeight:700,textAlign:"center"}}>{d}</span>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:4}}>
          {Array.from({length:startOffset}).map((_,i)=><div key={`e${i}`}/>)}
          {Array.from({length:daysInMonth}).map((_,i)=>{
            const day=i+1,date=new Date(yr,mo,day);
            const isToday=date.toDateString()===today.toDateString();
            const daySessions=sessions.filter(s=>new Date(s.date).toDateString()===date.toDateString());
            const hasMuscu=daySessions.some(s=>s.type==="muscu"||!s.type);
            const hasTabata=daySessions.some(s=>s.type==="tabata");
            const hasSess=daySessions.length>0;
            return(
              <div key={day} onClick={()=>{if(daySessions.length>0){setSelectedDate(prev=>prev&&prev.toDateString()===date.toDateString()?null:date);}}}
                style={{aspectRatio:"1",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:8,position:"relative",cursor:hasSess?"pointer":"default",
                  background:(hasMuscu&&hasTabata)?"#FF9800":hasMuscu?`${C.accent}25`:hasTabata?`${C.green}20`:isToday?C.faint:"transparent",
                  border:isToday?`1px solid ${C.accent}55`:"1px solid transparent"}}>
                <span style={{color:(hasMuscu&&hasTabata)?"#FF6F00":hasMuscu?C.accent:hasTabata?C.green:isToday?C.text:C.muted,fontWeight:hasSess||isToday?700:400,fontSize:13}}>{day}</span>
                {hasSess&&<div style={{position:"absolute",bottom:2,left:"50%",transform:"translateX(-50%)",width:4,height:4,borderRadius:"50%",background:(hasMuscu&&hasTabata)?"#FF6F00":hasMuscu?C.accent:C.green}}/>}
              </div>
            );
          })}
        </div>
        <div style={{display:"flex",gap:10,marginTop:10,paddingTop:10,borderTop:`1px solid ${C.border}`,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:`${C.accent}50`}}/><span style={{color:C.muted,fontSize:10}}>Musculation</span></div>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:`${C.green}50`}}/><span style={{color:C.muted,fontSize:10}}>Tabata</span></div>
          <div style={{display:"flex",alignItems:"center",gap:5}}><div style={{width:10,height:10,borderRadius:2,background:"#FF9800"}}/><span style={{color:C.muted,fontSize:10}}>Les deux</span></div>
        </div>
      </div>
      {/* Sessions du jour sélectionné */}
      {selectedDate&&(()=>{
        const daySessions=sessions.filter(s=>new Date(s.date).toDateString()===selectedDate.toDateString());
        if(daySessions.length===0)return null;
        return(
          <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${C.border}`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <p style={{color:C.muted,fontSize:11,letterSpacing:2,margin:0}}>
                SÉANCES DU {selectedDate.toLocaleDateString("fr-FR",{day:"numeric",month:"long",year:"numeric"}).toUpperCase()}
              </p>
              <button onClick={()=>setSelectedDate(null)} style={{background:"none",border:"none",color:C.muted,fontSize:20,cursor:"pointer",padding:0}}>×</button>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {daySessions.map(s=>{
                const isTabata=s.type==="tabata";
                if(isTabata){
                  return(
                    <div key={s.id} onClick={()=>setSel(s)} style={{background:C.card,borderRadius:13,padding:"13px 16px",border:`1px solid ${C.border}`,cursor:"pointer"}}>
                      <p style={{color:C.text,fontWeight:700,margin:"0 0 3px",fontSize:14}}>{s.programName}</p>
                      <p style={{color:C.muted,fontSize:12,margin:0}}>{s.rounds} rounds · {s.totalEx} exercices · {s.duration>0?`${s.duration} min`:"—"}</p>
                    </div>
                  );
                }
                const vol=s.exercises.reduce((a,ex)=>a+(ex.sets||[]).reduce((b,st)=>b+st.reps*st.weight,0),0);
                const nb=s.exercises.reduce((a,ex)=>a+(ex.sets||[]).length,0);
                return(
                  <div key={s.id} onClick={()=>setSel(s)} style={{background:C.card,borderRadius:13,padding:"13px 16px",border:`1px solid ${C.border}`,cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <p style={{color:C.text,fontWeight:700,margin:"0 0 3px",fontSize:14}}>{s.programName}</p>
                      <p style={{color:C.muted,fontSize:12,margin:0}}>{nb} séries · {s.duration}min</p>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <p style={{color:C.accent,fontWeight:800,margin:"0 0 1px",fontSize:14}}>{vol.toLocaleString()} kg</p>
                      <p style={{color:C.muted,fontSize:10,margin:0}}>VOLUME</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {!selectedDate&&sessions.length===0&&(
        <p style={{color:C.muted,textAlign:"center",marginTop:40}}>Aucune séance enregistrée</p>
      )}
      {!selectedDate&&sessions.length>0&&(
        <p style={{color:C.muted,textAlign:"center",marginTop:32,fontSize:13}}>Sélectionne un jour dans le calendrier pour voir tes séances.</p>
      )}

      
      {/* Menu Modal */}

    </div>
  );
}

// ─── PROGRESSION DES SÉRIES ───────────────────────────────────────────────────
function SeriesProgressPage({sessions, onBack, C}) {
  const [selEx, setSelEx] = useState(null);
  const [selGuide, setSelGuide] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selCat, setSelCat] = useState("all");

  const muscuSessions = sessions.filter(s=>s.type==="muscu"||!s.type);
  const hist = {};
  muscuSessions.forEach(s=>{
    const dl = new Date(s.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"});
    (s.exercises||[]).forEach(ex=>{
      if(!ex.sets?.length) return;
      const wt = ex.sets.filter(st=>st.weight>0);
      if(!wt.length) return;
      const mw = Math.max(...wt.map(st=>st.weight));
      if(!hist[ex.name]) hist[ex.name]=[];
      hist[ex.name].push({date:dl,fullDate:s.date,maxWeight:mw,sets:ex.sets});
    });
  });
  const seriesListAll = Object.entries(hist)
    .map(([name,data])=>({name,data:data.sort((a,b)=>new Date(a.fullDate)-new Date(b.fullDate))}))
    .sort((a,b)=>b.data.length-a.data.length);

  // Filtrer par recherche et catégorie muscle
  const EXCLUDED_CATS=["abdos","cardio","kettlebell","elastique"];
  const seriesList = seriesListAll.filter(({name})=>{
    const matchSearch = !searchQuery || name.toLowerCase().includes(searchQuery.toLowerCase());
    const cat=getExCategorie(name);
    if(EXCLUDED_CATS.includes(cat))return false;
    const matchCat = selCat==="all" || cat===selCat;
    return matchSearch && matchCat;
  });

  if(selGuide){
    return <MovementGuide exercise={selGuide} onClose={()=>setSelGuide(null)} C={C}/>;
  }

  if(selEx){
    const {name, data} = selEx;
    const mw = Math.max(...data.map(d=>d.maxWeight),1);
    const prog = data.length>=2 ? ((data.at(-1).maxWeight-data[0].maxWeight)/Math.max(data[0].maxWeight,1)*100).toFixed(1) : null;
    const disp = data.slice(-10);
    const guide = EXERCISE_GUIDE[name]||null;
    return(
      <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
        <div style={{padding:"0 0 16px",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={()=>setSelEx(null)} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer"}}>←</button>
          <div style={{flex:1,minWidth:0}}>
            <h2 style={{color:C.text,fontSize:17,fontWeight:900,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</h2>
            <p style={{color:C.muted,fontSize:12,margin:0}}>{data.length} séance{data.length>1?"s":""}</p>
          </div>
          {guide&&<button onClick={()=>setSelGuide(name)} style={{background:`${C.accent}15`,border:`1px solid ${C.accent}40`,borderRadius:10,padding:"7px 12px",color:C.accent,fontWeight:700,fontSize:12,cursor:"pointer",flexShrink:0}}>Fiche</button>}
        </div>
        {(()=>{
          const best1RM=data.flatMap(d=>d.sets||[]).filter(s=>s.weight>0&&s.reps>0).reduce((best,s)=>{const orm=Math.round(s.weight*(1+s.reps/30)*10)/10;return orm>best?orm:best;},0);
          return(
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
              {[{l:"MAX SÉANCE",v:`${data.at(-1).maxWeight} kg`},{l:"PROGRESSION",v:prog?`${prog>0?"+":""}${prog}%`:"—",c:prog>0?C.green:prog<0?C.red:C.muted},{l:"1RM ESTIMÉ",v:best1RM>0?`${best1RM} kg`:"—"},{l:"SÉANCES",v:data.length}].map(s=>(
                <div key={s.l} style={{background:C.card,borderRadius:13,padding:"12px 10px",textAlign:"center",border:`1px solid ${C.border}`}}>
                  <p style={{color:s.c||C.accent,fontSize:16,fontWeight:900,margin:"0 0 2px"}}>{s.v}</p>
                  <p style={{color:C.muted,fontSize:9,letterSpacing:2,margin:0}}>{s.l}</p>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{background:C.card,borderRadius:16,padding:"14px 14px 8px",border:`1px solid ${C.border}`,marginBottom:16}}>
          <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>POIDS MAX (kg)</p>
          <LineChart
            data={disp.map(d=>({val:d.maxWeight,date:d.date}))}
            height={96} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}
          />
        </div>
        {[...data].reverse().map((d,i)=>(
          <div key={i} style={{background:C.card,borderRadius:13,padding:"11px 13px",border:`1px solid ${C.border}`,marginBottom:7}}>
            <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
              <span style={{color:C.text,fontWeight:700,fontSize:13}}>{d.date}</span>
              <span style={{color:C.accent,fontWeight:800}}>{d.maxWeight} kg</span>
            </div>
            {d.sets.map((s,j)=>(
              <div key={j} style={{display:"flex",gap:7,alignItems:"center",marginBottom:3}}>
                <span style={{background:C.faint,color:C.muted,width:18,height:18,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700}}>{j+1}</span>
                <span style={{color:C.muted,fontSize:12}}>{s.reps} reps ×</span>
                <span style={{color:s.weight>0?C.accent:C.muted,fontWeight:700,fontSize:12}}>{s.weight>0?`${s.weight} kg`:"—"}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,display:"flex",flexDirection:"column"}}>
      {/* Header fixe */}
      <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 0",background:C.bg,flexShrink:0}}>
        <div style={{padding:"0 0 22px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",flexShrink:0}}>←</button>
        <div>
          <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 4px"}}>SUIVI</p>
          <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>Progression</h1>
        </div>
      </div>

      {/* Barre de recherche */}
      <div style={{position:"relative",marginBottom:12}}>
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input type="text" value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
          placeholder="Rechercher un exercice..."
          style={{width:"100%",padding:"11px 14px 11px 38px",borderRadius:12,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:16,outline:"none",boxSizing:"border-box"}}/>
        {searchQuery&&<button onClick={()=>setSearchQuery("")} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.muted,fontSize:18,cursor:"pointer",lineHeight:1}}>×</button>}
      </div>

      {/* Filtres muscles */}
      <div style={{display:"flex",gap:7,overflowX:"auto",paddingBottom:8,marginBottom:14,scrollbarWidth:"none"}}>
        {[{id:"all",label:"Tous"},...EXERCISE_CATEGORIES.filter(c=>!["abdos","cardio","kettlebell","elastique"].includes(c.id))].map(cat=>(
          <button key={cat.id} onClick={()=>setSelCat(cat.id)}
            style={{flexShrink:0,padding:"7px 13px",borderRadius:20,border:`1px solid ${selCat===cat.id?C.accent:C.border}`,background:selCat===cat.id?`${C.accent}18`:C.surface,color:selCat===cat.id?C.accent:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
            {cat.label}
          </button>
        ))}
      </div>
      </div>

      {/* Contenu scrollable */}
      <div style={{flex:1,overflowY:"auto",minHeight:0,WebkitOverflowScrolling:"touch",padding:"0 20px 100px"}}>
        {seriesListAll.length===0?(
        <div style={{background:C.card,borderRadius:18,padding:"32px 24px",textAlign:"center",border:`1px dashed ${C.border}`}}>
          <p style={{color:C.muted,fontSize:14}}>Aucun exercice trouvé pour cette recherche.</p>
        </div>
      ):(
        <div style={{display:"flex",flexDirection:"column",gap:9}}>
          {seriesList.map(({name,data})=>{
            const lat=data.at(-1),prev=data.length>=2?data.at(-2):null,delta=prev?lat.maxWeight-prev.maxWeight:null;
            const spark=data.slice(-6),sMax=Math.max(...spark.map(d=>d.maxWeight),1);
            return(
              <div key={name} onClick={()=>setSelEx({name,data})} style={{background:C.card,borderRadius:15,border:`1px solid ${C.border}`,padding:"12px 13px",cursor:"pointer"}}>
                <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:9}}>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{color:C.text,fontWeight:700,margin:"0 0 4px",fontSize:13,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</p>
                    <div style={{display:"flex",alignItems:"center",gap:7,marginTop:4}}>
                      <span style={{color:C.text,fontWeight:900,fontSize:17}}>{lat.maxWeight} kg</span>
                      {delta!==null&&<span style={{color:delta>0?C.green:delta<0?C.red:C.muted,fontWeight:700,fontSize:12}}>{delta>0?`+${delta}`:delta} kg</span>}
                      <span style={{color:C.muted,fontSize:11,marginLeft:"auto"}}>{data.length} séance{data.length>1?"s":""}</span>
                    </div>
                  </div>
                  <div style={{width:70,height:38,flexShrink:0}}>
                    <LineChart data={spark.map(d=>({val:d.maxWeight,date:""}))} compact={true} height={38} accentColor={C.accent} mutedColor={C.muted} cardBg={C.card} borderColor={C.border}/>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </div>
    </div>
  );
}

// ─── BASE DE DONNÉES EXERCICES ────────────────────────────────────────────────
function ExercicesBasePage({sessions, onBack, C}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selCat, setSelCat] = useState("all");
  const [selGuide, setSelGuide] = useState(null);

  // Historique pour indiquer les exercices déjà pratiqués
  const hist = {};
  sessions.filter(s=>s.type==="muscu"||!s.type).forEach(s=>{
    (s.exercises||[]).forEach(ex=>{
      if(!hist[ex.name]) hist[ex.name]=0;
      hist[ex.name]++;
    });
  });

  const allExNames = Object.keys(EXERCISE_GUIDE).sort((a,b)=>a.localeCompare(b,"fr"));
  const filtered = allExNames.filter(n=>{
    const matchSearch = !searchQuery || n.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCat = selCat==="all" || getExCategorie(n)===selCat;
    return matchSearch && matchCat;
  });

  if(selGuide){
    return <MovementGuide exercise={selGuide} onClose={()=>setSelGuide(null)} C={C}/>;
  }

  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,display:"flex",flexDirection:"column"}}>
      {/* Header fixe */}
      <div style={{padding:"calc(env(safe-area-inset-top) + 20px) 20px 0",background:C.bg,flexShrink:0}}>
        <div style={{padding:"0 0 22px",display:"flex",alignItems:"center",gap:12}}>
          <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",flexShrink:0}}>←</button>
          <div>
            <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 4px"}}>SUIVI</p>
            <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>Exercices</h1>
          </div>
        </div>

        {/* Barre de recherche */}
        <div style={{position:"relative",marginBottom:14}}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" style={{position:"absolute",left:13,top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={e=>setSearchQuery(e.target.value)}
            placeholder="Rechercher un exercice..."
            style={{width:"100%",padding:"11px 14px 11px 38px",borderRadius:12,border:`1px solid ${C.border}`,background:C.card,color:C.text,fontSize:16,outline:"none",boxSizing:"border-box"}}
          />
        </div>

        {/* Filtre catégories */}
        <div style={{display:"flex",gap:7,overflowX:"auto",paddingBottom:8,marginBottom:14,scrollbarWidth:"none"}}>
          {[{id:"all",label:"Tous"},...EXERCISE_CATEGORIES].map(cat=>(
            <button key={cat.id} onClick={()=>setSelCat(cat.id)}
              style={{flexShrink:0,padding:"7px 13px",borderRadius:20,border:`1px solid ${selCat===cat.id?C.accent:C.border}`,background:selCat===cat.id?`${C.accent}18`:C.surface,color:selCat===cat.id?C.accent:C.muted,fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu scrollable */}
      <div style={{flex:1,overflowY:"auto",minHeight:0,WebkitOverflowScrolling:"touch",padding:"0 20px 100px"}}>
        <p style={{color:C.muted,fontSize:11,letterSpacing:2,margin:"0 0 10px"}}>{filtered.length} EXERCICE{filtered.length!==1?"S":""}</p>

        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {filtered.map(name=>{
            const count = hist[name]||0;
            return(
              <button key={name} onClick={()=>setSelGuide(name)}
                style={{width:"100%",background:C.card,border:`1px solid ${C.border}`,borderRadius:13,padding:"13px 15px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",textAlign:"left"}}>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{color:C.text,fontWeight:700,fontSize:14,margin:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{name}</p>
                  {count>0&&<p style={{color:C.accent,fontSize:11,margin:"3px 0 0",fontWeight:600}}>Pratiqué · {count} séance{count>1?"s":""}</p>}
                </div>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" style={{flexShrink:0,marginLeft:10}}><path d="M9 18l6-6-6-6"/></svg>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// ─── SUIVI PAGE (fusion Stats + Historique) ──────────────────────────────────
function SuiviPage({sessions,measurements,onAddMeasurement,onDeleteMeasurement,prs,onSavePR,onDeletePR,onDeleteSession,totalXP=0,C}) {
  const [section,setSection]=useState(null); // null | "series" | "base" | "pr" | "mensurations" | "calendrier"

  // Passer les bonnes props aux sous-composants
  if(section==="series"){
    return <SeriesProgressPage
      sessions={sessions}
      onBack={()=>setSection(null)}
      C={C}
    />;
  }
  if(section==="base"){
    return <ExercicesBasePage
      sessions={sessions}
      onBack={()=>setSection(null)}
      C={C}
    />;
  }
  if(section==="pr"){
    return <StatsPage
      sessions={sessions}
      measurements={measurements}
      onAddMeasurement={onAddMeasurement}
      onDeleteMeasurement={onDeleteMeasurement}
      prs={prs}
      onSavePR={onSavePR}
      onDeletePR={onDeletePR}
      initialTab="pr"
      onBack={()=>setSection(null)}
      C={C}
    />;
  }
  if(section==="mensurations"){
    return <StatsPage
      sessions={sessions}
      measurements={measurements}
      onAddMeasurement={onAddMeasurement}
      onDeleteMeasurement={onDeleteMeasurement}
      prs={prs}
      onSavePR={onSavePR}
      onDeletePR={onDeletePR}
      initialTab="mens"
      onBack={()=>setSection(null)}
      C={C}
    />;
  }
  if(section==="calendrier"){
    return <HistoryPage sessions={sessions} onDeleteSession={onDeleteSession} onBack={()=>setSection(null)} C={C}/>;
  }
  if(section==="partage"){
    return <SharePage sessions={sessions} prs={prs} totalXP={totalXP} onBack={()=>setSection(null)} C={C}/>;
  }

  // ── Menu principal Suivi ────────────────────────────────────────────────────
  const menuItems=[
    {
      id:"series",
      label:"Progression",
      sub:"Évolution des poids par exercice",
      color:C.accent,
      svgPath:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
      svgLarge:<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
    },
    {
      id:"base",
      label:"Exercices",
      sub:"Base de données des exercices",
      color:C.accent,
      svgPath:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
      svgLarge:<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
    },
    {
      id:"pr",
      label:"PR",
      sub:"Records personnels",
      color:C.accent,
      svgPath:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
      svgLarge:<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>,
    },
    {
      id:"mensurations",
      label:"Mensurations",
      sub:"Poids & mesures corporelles",
      color:C.accent,
      svgPath:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h20M2 12l4-4M2 12l4 4M22 12l-4-4M22 12l-4 4"/><line x1="12" y1="5" x2="12" y2="19"/></svg>,
      svgLarge:<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12h20M2 12l4-4M2 12l4 4M22 12l-4-4M22 12l-4 4"/><line x1="12" y1="5" x2="12" y2="19"/></svg>,
    },
    {
      id:"calendrier",
      label:"Calendrier",
      sub:"Historique de tes séances",
      color:C.accent,
      svgPath:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
      svgLarge:<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    },
    {
      id:"partage",
      label:"Partage",
      sub:"Résumés hebdo & mensuels partageables",
      color:C.accent,
      svgPath:<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
      svgLarge:<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
    },
  ];

  // Quelques stats rapides pour la page d'accueil Suivi
  const muscuSessions=sessions.filter(s=>s.type==="muscu"||!s.type);
  const totalSessions=muscuSessions.length;
  const thisMonthSessions=muscuSessions.filter(s=>{
    const d=new Date(s.date);
    const now=new Date();
    return d.getMonth()===now.getMonth()&&d.getFullYear()===now.getFullYear();
  }).length;
  const prCount=Object.keys(prs||{}).length;
  const latestMeas=measurements&&measurements.length>0?measurements[measurements.length-1]:null;

  return(
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,display:"flex",flexDirection:"column",padding:"calc(env(safe-area-inset-top) + 20px) 20px calc(70px + env(safe-area-inset-bottom))",boxSizing:"border-box"}}>
      <div style={{padding:"0 0 22px"}}>
        <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 5px"}}>PROGRESSION</p>
        <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>Suivi</h1>
      </div>


      {/* Grille 2x3 */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
        {menuItems.map(item=>(
          <button key={item.id} onClick={()=>setSection(item.id)}
            style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:18,padding:"14px 14px 14px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"flex-start",gap:12,textAlign:"left",minHeight:0}}>
            <div style={{width:44,height:44,borderRadius:12,background:`${C.accent}15`,display:"flex",alignItems:"center",justifyContent:"center",color:C.accent,flexShrink:0}}>
              {item.svgLarge}
            </div>
            <div style={{flex:1,minWidth:0}}>
              <p style={{color:C.text,fontWeight:800,fontSize:15,margin:"0 0 4px"}}>{item.label}</p>
              <p style={{color:C.muted,fontSize:11,margin:0,lineHeight:1.4}}>{item.sub}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}



// ─── SHARE PAGE ───────────────────────────────────────────────────────────────
function SharePage({sessions, prs, totalXP=0, onBack, C}) {
  const [tab, setTab] = useState("hebdo"); // "hebdo" | "mensuel" | "export"
  const shareCardRef = useRef(null);
  const [activeSummary, setActiveSummary] = useState(null);
  const medal = getMedalByLevel(getLevelFromXP(totalXP));
  const [summaries, setSummaries] = useState({weekly:[], monthly:[]});
  const [sharing, setSharing] = useState(null); // id du résumé en cours de partage
  const [exportPeriod, setExportPeriod] = useState("90"); // "30" | "90" | "365" | "all"
  const isPremium = true; // TODO: remplacer par isPremium réel quand RevenueCat sera branché

  const generateCSV = () => {
    const now = new Date();
    const filtered = exportPeriod === "all" ? sessions : sessions.filter(s => {
      const d = new Date(s.date);
      const diffDays = (now - d) / (1000 * 60 * 60 * 24);
      return diffDays <= parseInt(exportPeriod);
    });

    const rows = [["Date", "Programme", "Type", "Exercice", "Série", "Répétitions", "Poids (kg)", "Durée (min)"]];
    filtered.forEach(s => {
      const date = new Date(s.date).toLocaleDateString("fr-FR");
      const prog = s.programName || "—";
      const type = s.type === "tabata" ? "Tabata" : "Musculation";
      if (s.type === "tabata") {
        rows.push([date, prog, type, "—", "—", "—", "—", s.duration || "—"]);
      } else {
        (s.exercises || []).forEach(ex => {
          (ex.sets || []).forEach((st, i) => {
            rows.push([date, prog, type, ex.name || "—", i + 1, st.reps || 0, st.weight || 0, i === 0 ? (s.duration || "—") : "—"]);
          });
        });
      }
    });

    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const label = exportPeriod === "all" ? "tout" : `${exportPeriod}j`;
    a.href = url;
    a.download = `calestan-export-${label}-${now.toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast(`${filtered.length} séance${filtered.length > 1 ? "s" : ""} exportée${filtered.length > 1 ? "s" : ""} ✓`);
  };

  // ── Génération des résumés ─────────────────────────────────────────────────
  useEffect(() => {
    const now = new Date();

    // ─ Semaines fermées (lundi 00:00 → dimanche 23:59)
    const weeklyList = [];
    // On remonte 52 semaines max
    for (let w = 1; w <= 52; w++) {
      const refMonday = new Date(now);
      // trouver le lundi de la semaine courante
      const dow = (refMonday.getDay()+6)%7; // 0=lundi
      refMonday.setDate(refMonday.getDate() - dow - w*7);
      refMonday.setHours(0,0,0,0);
      const refSunday = new Date(refMonday);
      refSunday.setDate(refMonday.getDate()+6);
      refSunday.setHours(23,59,59,999);

      // Semaine passée uniquement (dim terminé)
      if (refSunday >= now) continue;

      const weekSessions = sessions.filter(s => {
        const d = new Date(s.date);
        return d >= refMonday && d <= refSunday;
      });
      if (weekSessions.length === 0) continue;

      const muscuS = weekSessions.filter(s => s.type !== "tabata");
      const tabataS = weekSessions.filter(s => s.type === "tabata");
      const volume = muscuS.reduce((a,s) => a + s.exercises.reduce((b,ex) => b + (ex.sets||[]).reduce((c,st) => c + st.reps*st.weight, 0), 0), 0);
      const totalTime = weekSessions.reduce((a,s) => a + (s.duration||0), 0);
      const activeDays = new Set(weekSessions.map(s => new Date(s.date).toDateString())).size;

      // PR battus cette semaine
      const newPRsWeek = Object.entries(prs||{}).filter(([,pr]) => {
        const inHistory = (pr.history||[]).some(h => { const d = new Date(h.date); return d >= refMonday && d <= refSunday; });
        const inCurrent = pr.date && (() => { const d = new Date(pr.date); return d >= refMonday && d <= refSunday; })();
        return inHistory || inCurrent;
      }).map(([name, pr]) => ({ name, weight: pr.weight }));
      const prCount = newPRsWeek.length;

      // Exercice le plus fréquent
      const exFreq = {};
      muscuS.forEach(s => (s.exercises||[]).forEach(ex => { if(ex.name) exFreq[ex.name]=(exFreq[ex.name]||0)+1; }));
      const topEx = Object.entries(exFreq).sort((a,b)=>b[1]-a[1])[0]?.[0] || null;

      weeklyList.push({
        id: `week-${refMonday.toISOString().slice(0,10)}`,
        type: "weekly",
        label: `Sem. ${refMonday.toLocaleDateString("fr-FR",{day:"numeric",month:"short"})} – ${refSunday.toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}`,
        dateFrom: refMonday.toISOString(),
        dateTo: refSunday.toISOString(),
        muscuSessions: muscuS.length,
        tabataSessions: tabataS.length,
        totalSessions: weekSessions.length,
        volume,
        totalTime,
        activeDays,
        prCount,
        newPRs: newPRsWeek,
        topEx,
      });
    }
    weeklyList.reverse(); // plus récent en premier

    // ─ Mois fermés (1er → dernier jour)
    const monthlyList = [];
    for (let m = 1; m <= 24; m++) {
      const refFirst = new Date(now.getFullYear(), now.getMonth()-m, 1);
      refFirst.setHours(0,0,0,0);
      const refLast = new Date(now.getFullYear(), now.getMonth()-m+1, 0);
      refLast.setHours(23,59,59,999);

      if (refLast >= now) continue;

      const monthSessions = sessions.filter(s => {
        const d = new Date(s.date);
        return d >= refFirst && d <= refLast;
      });
      if (monthSessions.length === 0) continue;

      const muscuS = monthSessions.filter(s => s.type !== "tabata");
      const tabataS = monthSessions.filter(s => s.type === "tabata");
      const volume = muscuS.reduce((a,s) => a + s.exercises.reduce((b,ex) => b + (ex.sets||[]).reduce((c,st) => c + st.reps*st.weight, 0), 0), 0);
      const totalTime = monthSessions.reduce((a,s) => a + (s.duration||0), 0);
      const activeDays = new Set(monthSessions.map(s => new Date(s.date).toDateString())).size;

      const newPRsMonth = Object.entries(prs||{}).filter(([,pr]) => {
        const inHistory = (pr.history||[]).some(h => { const d = new Date(h.date); return d >= refFirst && d <= refLast; });
        const inCurrent = pr.date && (() => { const d = new Date(pr.date); return d >= refFirst && d <= refLast; })();
        return inHistory || inCurrent;
      }).map(([name, pr]) => ({ name, weight: pr.weight }));
      const prCount = newPRsMonth.length;

      const exFreq = {};
      muscuS.forEach(s => (s.exercises||[]).forEach(ex => { if(ex.name) exFreq[ex.name]=(exFreq[ex.name]||0)+1; }));
      const topEx = Object.entries(exFreq).sort((a,b)=>b[1]-a[1])[0]?.[0] || null;

      monthlyList.push({
        id: `month-${refFirst.toISOString().slice(0,7)}`,
        type: "monthly",
        label: refFirst.toLocaleDateString("fr-FR",{month:"long",year:"numeric"}),
        dateFrom: refFirst.toISOString(),
        dateTo: refLast.toISOString(),
        muscuSessions: muscuS.length,
        tabataSessions: tabataS.length,
        totalSessions: monthSessions.length,
        volume,
        totalTime,
        activeDays,
        prCount,
        newPRs: newPRsMonth,
        topEx,
      });
    }
    monthlyList.reverse();

    setSummaries({ weekly: weeklyList, monthly: monthlyList });
  }, [sessions, prs]);

  // ── Génération image html2canvas ──────────────────────────────────────────
  const generateAndShare = async (summary) => {
    setSharing(summary.id);
    setActiveSummary(summary);
    await new Promise(r => setTimeout(r, 100));
    try {
      if (!shareCardRef.current) return;
      const canvas = await html2canvas(shareCardRef.current, { scale: 2, useCORS: true, backgroundColor: null });
      canvas.toBlob(async (blob) => {
        const file = new File([blob], `calestan-${summary.id}.png`, { type: "image/png" });
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: `Mon récap Calestan – ${summary.label}` });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `calestan-${summary.id}.png`;
          a.click();
          URL.revokeObjectURL(url);
        }
      }, "image/png");
    } catch(e) {
      console.error("Share error:", e);
    }
    setSharing(null);
    setActiveSummary(null);
  };

  const list = tab === "hebdo" ? summaries.weekly : summaries.monthly;

  return (
    <div style={{position:"fixed",inset:0,overscrollBehavior:"none",background:C.bg,overflowY:"auto",WebkitOverflowScrolling:"touch",padding:"calc(env(safe-area-inset-top) + 20px) 20px 100px",boxSizing:"border-box"}}>
      {/* Header */}
      <div style={{padding:"0 0 20px",display:"flex",alignItems:"center",gap:12}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:C.muted,fontSize:24,cursor:"pointer",padding:"0 4px 0 0",flexShrink:0}}>←</button>
        <div>
          <p style={{color:C.muted,fontSize:11,letterSpacing:3,margin:"0 0 4px"}}>MES RÉSUMÉS</p>
          <h1 style={{color:C.text,fontSize:26,fontWeight:900,margin:0}}>Partage</h1>
        </div>
      </div>

      {/* Tabs hebdo / mensuel / export */}
      <div style={{display:"flex",gap:8,marginBottom:20,background:C.card,borderRadius:14,padding:4,border:`1px solid ${C.border}`}}>
        {[{id:"hebdo",label:"Semaines"},{id:"mensuel",label:"Mois"},{id:"export",label:"Export",premium:true}].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{flex:1,padding:"10px 0",borderRadius:11,border:"none",background:tab===t.id?C.accent:"transparent",
              color:tab===t.id?"white":C.muted,fontWeight:700,fontSize:13,cursor:"pointer",transition:"all 0.2s",display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
            <span>{t.label}</span>
            {t.premium&&!isPremium&&<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>}
          </button>
        ))}
      </div>

      {/* Info */}
      {tab !== "export" && (
      <p style={{color:C.muted,fontSize:12,margin:"0 0 16px",lineHeight:1.5}}>
        {tab==="hebdo"
          ? "Résumés des semaines terminées (lundi → dimanche). Disponibles dès le lundi suivant."
          : "Résumés des mois terminés. Disponibles dès le 1er du mois suivant."}
      </p>
      )}

      {/* Tab Export CSV */}
      {tab === "export" && (
        isPremium ? (
        <div>
          <p style={{color:C.muted,fontSize:12,margin:"0 0 20px",lineHeight:1.5}}>
            Télécharge tes séances au format CSV, ouvrable dans Excel, Google Sheets ou Numbers.
          </p>
          <p style={{color:C.muted,fontSize:10,letterSpacing:3,margin:"0 0 10px"}}>PÉRIODE</p>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:24}}>
            {[{v:"30",label:"30 derniers jours"},{v:"90",label:"90 derniers jours"},{v:"365",label:"Cette année"},{v:"all",label:"Tout l'historique"}].map(opt => (
              <button key={opt.v} onClick={()=>setExportPeriod(opt.v)}
                style={{padding:"14px 10px",borderRadius:14,border:`1.5px solid ${exportPeriod===opt.v?C.accent:C.border}`,
                  background:exportPeriod===opt.v?`${C.accent}15`:C.card,
                  color:exportPeriod===opt.v?C.accent:C.muted,fontWeight:700,fontSize:13,cursor:"pointer",textAlign:"center"}}>
                {opt.label}
              </button>
            ))}
          </div>
          <div style={{background:C.card,borderRadius:14,padding:"14px 16px",border:`1px solid ${C.border}`,marginBottom:20}}>
            <p style={{color:C.muted,fontSize:11,margin:"0 0 6px",letterSpacing:1}}>CONTENU DU FICHIER</p>
            <p style={{color:C.text,fontSize:13,margin:0,lineHeight:1.6}}>Date · Programme · Type · Exercice · Série · Répétitions · Poids (kg) · Durée</p>
          </div>
          <button onClick={generateCSV}
            style={{width:"100%",padding:"16px",background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:16,
              color:"white",fontWeight:900,fontSize:15,cursor:"pointer",letterSpacing:0.5}}>
            ↓ Télécharger le CSV
          </button>
        </div>
        ) : (
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"40px 20px",textAlign:"center",gap:16}}>
          <div style={{width:56,height:56,borderRadius:16,background:`${C.accent}15`,display:"flex",alignItems:"center",justifyContent:"center"}}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
          <p style={{color:C.text,fontWeight:800,fontSize:16,margin:0}}>Export CSV</p>
          <p style={{color:C.muted,fontSize:13,margin:0,lineHeight:1.5}}>Exporte toutes tes séances vers Excel,<br/>Google Sheets ou Numbers.</p>
          <span style={{fontSize:11,color:C.accent,fontWeight:700,letterSpacing:1,background:`${C.accent}15`,padding:"4px 12px",borderRadius:20}}>PREMIUM</span>
        </div>
        )
      )}

      {/* Liste des résumés */}
      {tab !== "export" && (
        list.length === 0 ? (
        <div style={{textAlign:"center",padding:"40px 20px"}}>
          <div style={{fontSize:40,marginBottom:12}}>📊</div>
          <p style={{color:C.muted,fontSize:14}}>Aucun résumé disponible pour l'instant.<br/>Continue à t'entraîner !</p>
        </div>
        ) : (
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          {list.map(summary => (
            <div key={summary.id} style={{background:C.card,borderRadius:18,padding:"16px 18px",border:`1px solid ${C.border}`}}>
              {/* Label période */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:38,height:38,borderRadius:11,background:"#FF6B3518",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="#FF6B35" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                    </svg>
                  </div>
                  <div>
                    <p style={{color:C.text,fontWeight:800,fontSize:14,margin:0,textTransform:"capitalize"}}>{summary.label}</p>
                    <p style={{color:C.muted,fontSize:11,margin:0}}>{summary.activeDays} jour{summary.activeDays>1?"s":""} actif{summary.activeDays>1?"s":""}</p>
                  </div>
                </div>
                <button onClick={()=>generateAndShare(summary)}
                  disabled={sharing===summary.id}
                  style={{background:`linear-gradient(135deg,${C.accent},${C.accent2})`,border:"none",borderRadius:12,padding:"8px 14px",
                    color:"white",fontWeight:700,fontSize:13,cursor:"pointer",opacity:sharing===summary.id?0.6:1,flexShrink:0}}>
                  {sharing===summary.id ? "..." : "Partager"}
                </button>
              </div>

              {/* Mini stats */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8}}>
                {[
                  {l:"Séances",v:summary.totalSessions},
                  {l:"Volume",v:summary.volume>=1000?`${(summary.volume/1000).toFixed(1)}T`:(summary.volume>0?`${summary.volume}kg`:"—")},
                  {l:"Temps",v:summary.totalTime>=60?`${Math.floor(summary.totalTime/60)}h${String(summary.totalTime%60).padStart(2,"0")}`:(summary.totalTime>0?`${summary.totalTime}m`:"—")},
                  {l:"PR",v:summary.prCount>0?`${summary.prCount}`:"—"},
                ].map(s=>(
                  <div key={s.l} style={{background:C.faint,borderRadius:10,padding:"8px 4px",textAlign:"center",border:`1px solid ${C.border}`}}>
                    <p style={{color:C.accent,fontSize:13,fontWeight:900,margin:"0 0 1px"}}>{s.v}</p>
                    <p style={{color:C.muted,fontSize:9,letterSpacing:1,margin:0}}>{s.l.toUpperCase()}</p>
                  </div>
                ))}
              </div>

              {/* Top exercice */}
              {summary.topEx && (
                <p style={{color:C.muted,fontSize:11,margin:"10px 0 0",textAlign:"center"}}>
                  Exercice favori · <span style={{color:C.text,fontWeight:700}}>{summary.topEx}</span>
                </p>
              )}
            </div>
          ))}
        </div>
        )
      )}
      {activeSummary && (
        <div ref={shareCardRef} style={{position:'fixed',left:'-9999px',top:0,width:390,height:693,overflow:'hidden',zIndex:-1,pointerEvents:'none'}}>
          <ShareCardPeriod summary={activeSummary} medal={medal} />
        </div>
      )}
    </div>
  );
}

export { MeasModal, StatsPage, SessionSummaryPage, HistoryPage, SeriesProgressPage, ExercicesBasePage, SuiviPage };
